import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  createProject,
  createRevisionOp,
  deserializeProject,
  diffProjects,
  latestRevision,
  restoreRevisionOps,
  FILE_EXTENSION,
  OPEN_EXTENSIONS,
  History,
  MIME_TYPE,
  serializeProject,
  type CustomSymbol,
  type Project,
  type Sheet,
  type ProjectMeta,
} from '@elec/domain';
import {
  createAiService,
  extractionToTransaction,
  partDeviceProps,
  type BoardExtraction,
  type FixTarget,
  type ImageMediaType,
  isAiUnavailable,
} from '@elec/ai';
import { prepareAiImage } from './photos.ts';

const PHOTO_MEDIA: Record<string, ImageMediaType> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};
import { analyse, boardRootSheet, staleMultiLines } from '@elec/analysis';
import { balancedPhases } from '@elec/calc';
import {
  calibrateOps,
  Editor,
  generateBoardOps,
  generateBoardLayoutOps,
  generateMultiLineOps,
  isMacroLibraryFile,
  makeMacro,
  type Macro,
  numberTerminalsOps,
  numberWiresOps,
  pvInstallationOps,
  type PvSpec,
  type BoardSpec,
  isClipboardData,
  unplacedPoints,
  earthElectrodeOps,
  attachPointOps,
  earthSocketsOps,
  estimateLengthsOps,
  blankingOps,
  labelStrips,
  northArrowOps,
  cableRoutesOps,
  renameTagOps,
  renumberOps,
  roomAroundOps,
} from '@elec/editor';
import {
  contentLocale,
  detectLocale,
  localeTag,
  LOCALES,
  pick,
  say,
  translator,
  type Locale,
  type MessageKey,
} from '@elec/i18n';
import { paperSize } from '@elec/layout';
import { isSymbolLibraryFile } from '@elec/symbols';
import { assignBrandOps, BRANDS } from '@elec/catalog';
import {
  clearRecovery,
  cloudKey,
  deleteLibraryEntry,
  listRecoveries,
  loadLibraryEntry,
  loadSession,
  persistStorage,
  putStoredAsset,
  saveLibraryEntry,
  saveRecovery,
  saveSession,
  storedAssets,
  type LibraryEntry,
  type Platform,
  type RecoveryEntry,
  type SessionEntry,
} from '@elec/platform';
import { generateBenchmarkProject } from '@elec/renderer';
import { checkProject, earthNeeds, ruleInfo, type Finding } from '@elec/rules';
import {
  generateDemoProject,
  INSERT_TEMPLATES,
  type InsertTemplate,
  type ProjectTemplate,
} from '@elec/samples';
import {
  applyTransaction,
  assetRefs,
  hasSurvey,
  migrate,
  newId,
  sheetsInOrder,
  type Id,
  type Op,
  tr,
} from '@elec/domain';
import { addBoardOp, generateFromSurvey, LABELS as SURVEY_LABELS } from '@elec/survey';
import { parseSurveyHash, Survey, surveyHash, type SurveyRoute, type SurveyStatus } from './Survey.tsx';
import { BoardImportDialog } from './AiDialogs.tsx';
import { AssistantPanel, SketchDialog, TemplateDialog } from './Phase3Dialogs.tsx';
import { renameTargets, type FindingButton } from './findingActions.ts';
import { quickFix, type QuickFixKind } from './quickFixes.ts';

const QUICK_TX: Record<QuickFixKind, MessageKey> = {
  move30: 'checks.quickMove30',
  typeA: 'checks.quickTypeA',
  main300: 'checks.quickMain300',
  rating: 'checks.quickRating',
  section: 'checks.quickSection',
};
import { parseSidebar, showSection, Sidebar, type SideSection, type SidebarState } from './Sidebar.tsx';
import { NameDialog, RevisionsDialog } from './RevisionsDialog.tsx';
import { AboutDialog, APP_VERSION } from './AboutDialog.tsx';
import { SettingsDialog } from './SettingsDialog.tsx';
import { BackupSection, useAutoBackup } from './BackupSection.tsx';
import { startUsage } from './usage.ts';
import { WelcomeDialog } from './WelcomeDialog.tsx';
import { Tour } from './Tour.tsx';
import type { TourApp } from './tourSteps.ts';
import { CloudManager } from './cloud.ts';
import { AccountButton, CloudDialog, ShareDialog, type AccountView } from './CloudDialogs.tsx';
import { RemediationDialog } from './Remediation.tsx';
import { ChangesDialog } from './ChangesDialog.tsx';
import { PvDialog } from './PvDialog.tsx';
import { nearestTag, PinDialog, PinsDialog, pinsToOps } from './Review.tsx';
import { OfferDialog, OfferView } from './Offers.tsx';
import { AiSignInPrompt, type AiFeature } from './AiUpsell.tsx';
import { CloudClient, type AutosaveSummary, type LibraryItem, type ReviewPin } from '@elec/sync';
import { ACCOUNT_AUTOSAVE_MS, AUTOSAVE_MS, mergeAutosaves, newerOnDevice } from './autosave.ts';
import { SymbolEditorDialog } from './SymbolEditor.tsx';
import { useAssets } from './assets.ts';
import { BottomPanel, type PanelTab, type RuleReporter } from './BottomPanel.tsx';
import { SignoffDialog } from './Signoff.tsx';
import { ClientsDialog } from './ClientsDialog.tsx';
import { PickListDialog } from './PickList.tsx';
import { HomeView } from './HomeView.tsx';
import { EnergyDialog } from './EnergyDialog.tsx';
import { rememberSite } from './siteIndex.ts';
import { Canvas, type BenchResult, type CanvasHandle } from './Canvas.tsx';
import { Inspector, type InspectorHandle } from './Inspector.tsx';
import { Palette } from './Palette.tsx';
import { CalibrateDialog, DxfImportDialog, RoomScanDialog } from './PlanDialogs.tsx';
import { QuoteDialog } from './QuoteDialog.tsx';
import { InvoiceDialog } from './InvoiceDialog.tsx';
import { InspectionsDialog } from './Inspections.tsx';
import { MeasurementsDialog } from './Measurements.tsx';
import type { ChangeReport, Quote } from '@elec/export';
import { importPlan, isCadFile, isRoomScanFile, PLAN_EXTENSIONS } from './planImport.ts';
import {
  CadFormatError,
  parseDxf,
  parseRoomScan,
  planSize,
  RoomScanError,
  serializePlan,
  VECTOR_PLAN_MIME,
  type DxfDrawing,
  type RoomScan,
  type VectorPlan,
} from '@elec/plan';
import { ProjectInfoDialog } from './ProjectInfo.tsx';
import { SheetTabs } from './SheetTabs.tsx';
import { Icon, IconButton, Menu, setCancelLabel, useConfirm } from './ui.tsx';

declare global {
  interface Window {
    /** Test hooks used by the end-to-end checks and the benchmark script. */
    __elec?: {
      benchmark(count: number, durationMs?: number): Promise<BenchResult & { elements: number }>;
      project(): Project;
      editor: Editor;
      load(p: Project): void;
      exportPdf(): Promise<Uint8Array>;
      toClient(x: number, y: number): { x: number; y: number };
      findings(): ReturnType<typeof checkProject>;
      assetIds(): string[];
      cloud(): import('./cloud.ts').CloudState;
      /** Other people's pointers on the open sheet (real-time cursors). */
      peers(): Array<{ name: string; x: number; y: number }>;
    };
  }
}

/** AI backend URL (services/api). Without it the app runs the AI features in demo mode. */
const AI_URL = (import.meta.env.VITE_AI_URL as string | undefined) || undefined;
/** The PDF library is large: load the exporters only when needed. */
const exporters = () => import('@elec/export');
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = isMac ? '⌘' : 'Ctrl+';

/** Token of a password reset link (#/reset/<token>), removed from the address bar once read. */
/** Web Lock held by the tab that has this project open. */
const openLock = (kind: string, ref: string) => `tablo-open:${kind}:${ref}`;

/** Token of an emailed link (#/reset/<token>, #/confirm/<token>), taken out of the address bar. */
function takeAuthToken(kind: 'reset' | 'confirm'): string | null {
  const token = new RegExp(`^#/${kind}/([\\w-]+)`).exec(location.hash)?.[1] ?? null;
  if (token) {
    const phone = window.matchMedia?.('(max-width: 700px)').matches;
    window.history.replaceState(null, '', phone ? '#/survey' : location.pathname + location.search);
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  }
  return token;
}

/** What "Fix with the assistant" asks, in the project's language. */
function fixRequest(f: Finding, locale: 'fr' | 'en' | 'nl'): string {
  const msg = f.message[locale];
  return tr(
    locale,
    `Corrige ce problème : « ${msg} » (${f.ruleId}).`,
    `Fix this problem: "${msg}" (${f.ruleId}).`,
    `Los dit probleem op: "${msg}" (${f.ruleId}).`,
  );
}

export function App({ platform }: { platform: Platform }) {
  const [locale, setLocale] = useState<Locale>(
    () => (platform.getPref('locale') as Locale | null) ?? detectLocale(platform.preferredLanguages()),
  );
  const t = useMemo(() => translator(locale), [locale]);
  /** The language of drawings, templates, the assistant and file names (French for German). */
  const cl = contentLocale(locale);
  const [history] = useState(() => new History(createProject({ locale })));
  const [editor] = useState(() => new Editor(history, Object.keys(history.project.sheets)[0]!));
  const project = useSyncExternalStore(
    (cb) => history.subscribe(cb),
    () => history.project,
  );
  useSyncExternalStore(
    (cb) => editor.subscribe(cb),
    () => editor.stateVersion,
  );
  const [zoom, setZoom] = useState(3);
  const [file, setFile] = useState<{ name: string; handle?: unknown } | null>(null);
  const [savedProject, setSavedProject] = useState<Project>(project);
  const [message, setMessage] = useState<string | null>(null);
  const [autosavedAt, setAutosavedAt] = useState<number | null>(null);
  /** Autosaves found on this device and in the account, and the one picked in the recovery banner. */
  const [localSaves, setLocalSaves] = useState<RecoveryEntry[]>([]);
  const [accountSaves, setAccountSaves] = useState<AutosaveSummary[]>([]);
  const [recoveryPick, setRecoveryPick] = useState(0);
  const [recoveryDismissed, setRecoveryDismissed] = useState(false);
  const [showInfo, setShowInfo] = useState<boolean | 'missing'>(false);
  const [showClients, setShowClients] = useState(false);
  const [showPick, setShowPick] = useState(false);
  const [showEnergy, setShowEnergy] = useState(false);
  const [panel, setPanel] = useState<PanelTab | null>(null);
  const [aiDialog, setAiDialog] = useState<'board' | 'sketch' | null>(null);
  /** Right sidebar: folded or not, its open sections (kept between sessions). */
  const [side, setSideState] = useState<SidebarState>(() => parseSidebar(platform.getPref('sidebar')));
  const setSide = useCallback(
    (s: SidebarState) => {
      setSideState(s);
      platform.setPref('sidebar', JSON.stringify({ folded: s.folded, open: s.open }));
    },
    [platform],
  );
  /** Left symbol palette folded to a rail of icons (kept between sessions). */
  const [palFolded, setPalFoldedState] = useState(() => platform.getPref('palette.folded') === '1');
  const setPalFolded = useCallback(
    (f: boolean) => {
      setPalFoldedState(f);
      platform.setPref('palette.folded', f ? '1' : '0');
    },
    [platform],
  );
  const palFoldedRef = useRef(palFolded);
  palFoldedRef.current = palFolded;
  const sideRef = useRef(side);
  sideRef.current = side;
  /** Open a section of the sidebar (beside the rail when folded), then run `then` once it shows. */
  const openSection = useCallback(
    (id: SideSection, then?: () => void) => {
      setSide(showSection(sideRef.current, id));
      requestAnimationFrame(() => {
        document.querySelector(`.sidebar [data-section="${id}"]`)?.scrollIntoView?.({ block: 'nearest' });
        then?.();
      });
    },
    [setSide],
  );
  const [showTemplates, setShowTemplates] = useState(false);
  const [showRevisions, setShowRevisions] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showWelcome, setShowWelcome] = useState(() => !platform.getPref('welcomed'));
  const closeWelcome = () => {
    setShowWelcome(false);
    platform.setPref('welcomed', '1');
  };
  /** Guided tutorial running (Tour.tsx). */
  const [tour, setTour] = useState(false);
  const [compareRev, setCompareRev] = useState<Id | null>(null);
  /** Compared to this revision instead of now: shown read-only on the canvas (as a preview). */
  const [compareTo, setCompareTo] = useState<Id | null>(null);
  const [changes, setChanges] = useState<{ report: ChangeReport | null } | null>(null);
  const [symbolEditor, setSymbolEditor] = useState<{ id?: string } | null>(null);
  const [askMacroName, setAskMacroName] = useState(false);
  const [macros, setMacros] = useState<Macro[]>(() => {
    try {
      const v = JSON.parse(platform.getPref('macros') ?? '[]');
      return Array.isArray(v) ? v : [];
    } catch {
      return [];
    }
  });
  const saveMacros = (list: Macro[]) => {
    setMacros(list);
    platform.setPref('macros', JSON.stringify(list));
  };
  const canvas = useRef<CanvasHandle>(null);
  const inspector = useRef<InspectorHandle>(null);
  const { ask, node: confirmNode } = useConfirm();
  const assets = useAssets();
  const [cloud] = useState(() => new CloudManager(platform));
  const cloudState = useSyncExternalStore(
    (cb) => cloud.subscribe(cb),
    () => cloud.state,
  );
  const live = cloudState.live;
  // Real-time cursors in a shared project (the canvas draws others' pointers, sends ours).
  const livePeers = !!live;
  const peers = useMemo(
    () =>
      livePeers
        ? {
            list: (sheetId: string) => cloud.peerCursors(sheetId),
            move: (sheetId: string | null, x?: number, y?: number) => cloud.moveCursor(sheetId, x, y),
            subscribe: (fn: () => void) => cloud.onCursors(fn),
          }
        : undefined,
    [cloud, livePeers],
  );
  const ai = useMemo(
    () =>
      AI_URL
        ? createAiService(AI_URL, () => cloud.client?.token ?? null, { transcribe: cloudState.stt })
        : cloudState.ai && cloud.client
          ? createAiService(cloud.client.baseUrl, () => cloud.client?.token ?? null, {
              transcribe: cloudState.stt,
            })
          : createAiService(undefined),
    [cloudState.ai, cloudState.stt, cloud],
  );
  const [cloudDialog, setCloudDialog] = useState<'cloud' | 'share' | null>(null);
  /** Where the account dialog opens, when the account menu picked it. */
  const [cloudStart, setCloudStart] = useState<AccountView | undefined>();
  useEffect(() => {
    if (cloudDialog !== 'cloud') setCloudStart(undefined);
  }, [cloudDialog]);
  // Back from Billit's login page (docs/decisions/0013): #/billit/connected or #/billit/failed.
  useEffect(() => {
    const m = /^#\/billit\/(connected|failed)/.exec(location.hash);
    if (!m) return;
    window.history.replaceState(null, '', location.pathname + location.search);
    setMessage(m[1] === 'connected' ? t('billit.connectedNotice') : t('billit.failedNotice'));
  }, []);
  // Back from Teamleader's login page: #/teamleader/connected or #/teamleader/failed.
  useEffect(() => {
    const m = /^#\/teamleader\/(connected|failed)/.exec(location.hash);
    if (!m) return;
    window.history.replaceState(null, '', location.pathname + location.search);
    setMessage(m[1] === 'connected' ? t('teamleader.connected') : t('teamleader.failed'));
  }, []);
  // Password reset link from the email: #/reset/<token>. Read once, then out of the address bar.
  const [resetToken, setResetToken] = useState<string | null>(() => takeAuthToken('reset'));
  // Email confirmation link from the sign-up email: #/confirm/<token>.
  const [confirmToken, setConfirmToken] = useState<string | null>(() => takeAuthToken('confirm'));
  useEffect(() => {
    if (resetToken || confirmToken) setCloudDialog('cloud');
    // The link opened in a tab where Tablo was already running.
    const f = () => {
      const reset = takeAuthToken('reset');
      const confirm = reset ? null : takeAuthToken('confirm');
      if (!reset && !confirm) return;
      if (reset) setResetToken(reset);
      if (confirm) setConfirmToken(confirm);
      setCloudDialog('cloud');
    };
    window.addEventListener('hashchange', f);
    return () => window.removeEventListener('hashchange', f);
  }, []);
  /** Read-only view of a shared link (#/view/<token>). */
  const [viewer, setViewer] = useState<{
    revision: string | null;
    /** A link for an inspection body: remarks pinned on the drawings. */
    review?: { token: string; pins: ReviewPin[] };
  } | null>(null);
  /** Pinning a remark: the next click on the sheet places it. */
  const [picking, setPicking] = useState(false);
  const [pinDraft, setPinDraft] = useState<{ sheetId: Id; x: number; y: number; ref: string } | null>(null);
  const [showPins, setShowPins] = useState(false);
  /** The project's remarks pinned online, not yet taken over (installer). */
  const [pendingPins, setPendingPins] = useState<ReviewPin[]>([]);
  const [teamItems, setTeamItems] = useState<LibraryItem[]>([]);
  const [showQuote, setShowQuote] = useState(false);
  /** After the drawing: test results, inspection reports and remarks, invoices. */
  const [afterDialog, setAfterDialog] = useState<
    'measurements' | 'inspections' | 'invoices' | 'signoff' | null
  >(null);
  /** Quote an invoice is being started from. */
  const [invoiceQuote, setInvoiceQuote] = useState<Quote | undefined>(undefined);
  const [remedyFor, setRemedyFor] = useState<Id | null>(null);
  const [showPv, setShowPv] = useState(false);
  const [offerQuote, setOfferQuote] = useState<Quote | null>(null);
  /** DXF file read, waiting for the import choices. */
  const [dxf, setDxf] = useState<{ drawing: DxfDrawing } | null>(null);
  const [roomScan, setRoomScan] = useState<RoomScan | null>(null);
  const [canScan, setCanScan] = useState(false);
  useEffect(() => {
    void platform.roomScan?.supported().then(setCanScan);
  }, [platform]);
  /** Key of the open project in this device's library (survey projects), else null. */
  const [libKey, setLibKey] = useState<string | null>(null);
  const storedIds = useRef(new Set<string>());

  // Site survey mode lives in the URL hash (#/survey…); phones start in it.
  const [hash, setHash] = useState(() => {
    if (!location.hash && window.matchMedia?.('(max-width: 700px)').matches)
      window.history.replaceState(null, '', '#/survey');
    return location.hash;
  });
  useEffect(() => {
    const f = () => setHash(location.hash);
    window.addEventListener('hashchange', f);
    window.addEventListener('popstate', f);
    return () => {
      window.removeEventListener('hashchange', f);
      window.removeEventListener('popstate', f);
    };
  }, []);
  const goSurvey = useCallback((r: SurveyRoute, replace = false) => {
    const h = surveyHash(r);
    if (replace) window.history.replaceState(null, '', h);
    else window.history.pushState(null, '', h);
    setHash(h);
  }, []);

  const sheet = project.sheets[editor.sheetId] ?? Object.values(project.sheets)[0]!;
  // Electrical analysis and checks against the project's standard, recomputed when it changes.
  const analysis = useMemo(() => analyse(project), [project]);
  const findings = useMemo(() => checkProject(project, analysis), [project, analysis]);
  const unplaced = useMemo(
    () => (sheet.kind === 'position-plan' ? unplacedPoints(project, analysis) : []),
    [project, analysis, sheet.kind],
  );
  const nErrors = findings.filter((f) => f.severity === 'error').length;
  const nWarnings = findings.filter((f) => f.severity === 'warning').length;
  // A live cloud project is saved when the server has every change.
  const dirty = viewer ? false : live ? live.pending > 0 : project !== savedProject;
  // File names: no characters that file systems refuse ("Relevé du 18/10/2026" → "…18-10-2026").
  const baseName =
    (file?.name ?? project.meta.title ?? '')
      .replace(/\.(tablo|elec)$/i, '')
      .replace(/[\\/:*?"<>|]+/g, '-')
      .trim() || say(locale, 'projet', 'project', 'project', 'Projekt');

  // ---------- language ----------
  useEffect(() => {
    document.documentElement.lang = locale;
    platform.setPref('locale', locale);
    setCancelLabel(t('dialog.cancel'));
    editor.labels = {
      move: t('tx.move'),
      add: (name) => t('tx.add', { name }),
      wire: t('tx.wire'),
      delete: t('tx.delete'),
      rotate: t('tx.rotate'),
      mirror: t('tx.mirror'),
      paste: t('tx.paste'),
      edit: t('tx.edit'),
      roomName: t('room.default'),
    };
  }, [locale, platform, t, editor]);

  useEffect(() => {
    document.title = `${baseName}${dirty ? ' •' : ''} — ${t('app.name')}`;
  }, [baseName, dirty, t]);

  // ---------- load / new ----------
  const load = useCallback(
    (
      p: Project,
      f: { name: string; handle?: unknown } | null = null,
      markSaved = true,
      files: Record<string, Uint8Array> = {},
    ) => {
      cloud.leave(history);
      setLibKey(null);
      void assets.replaceAll(files, p);
      history.reset(p);
      if (markSaved) setSavedProject(p);
      editor.setSheet(Object.values(p.sheets).sort((a, b) => a.order - b.order)[0]!.id);
      editor.select([]);
      editor.setTool({ kind: 'select' });
      setFile(f);
      requestAnimationFrame(() => canvas.current?.fitPage());
    },
    [history, editor, assets, cloud],
  );

  const confirmDiscard = async () =>
    !dirty ||
    (await ask({
      title: t('confirm.discardTitle'),
      body: t('confirm.discard'),
      confirm: t('dialog.discard'),
      danger: true,
    }));

  const onNew = async () => {
    if (!(await confirmDiscard())) return;
    forgetAutosave(history.project.id);
    load(createProject({ locale }));
  };

  /** The tutorial works in a new, empty project (after asking to discard unsaved work). */
  const startTour = async () => {
    if (!(await confirmDiscard())) return;
    forgetAutosave(history.project.id);
    load(createProject({ locale }));
    setPanel(null);
    setTour(true);
  };

  const onOpen = async () => {
    if (!(await confirmDiscard())) return;
    try {
      const f = await platform.openFile({ extensions: OPEN_EXTENSIONS, description: 'Tablo' });
      if (!f) return;
      const opened = deserializeProject(f.bytes);
      forgetAutosave(history.project.id);
      load(opened.project, { name: f.name, handle: f.handle }, true, opened.assets);
    } catch (e) {
      setMessage(t('error.open', { msg: (e as Error).message }));
    }
  };

  const onSave = async (as = false) => {
    try {
      const bytes = serializeProject({ project, assets: assets.forProject(project) });
      const r = await platform.saveFile(bytes, {
        suggestedName: `${baseName}.${FILE_EXTENSION}`,
        extensions: [FILE_EXTENSION],
        description: 'Tablo',
        mime: MIME_TYPE,
        handle: as ? undefined : file?.handle,
      });
      if (!r) return;
      setFile(r);
      setSavedProject(project);
      setMessage(t('status.saved'));
    } catch (e) {
      setMessage(t('error.save', { msg: (e as Error).message }));
    }
  };

  // ---------- exports ----------
  const saveExport = async (bytes: Uint8Array, name: string, ext: string, mime: string) => {
    const r = await platform.saveFile(bytes, {
      suggestedName: name,
      extensions: [ext],
      description: ext.toUpperCase(),
      mime,
    });
    if (r) setMessage(t('status.exported', { name: r.name }));
  };

  const exportReport = async (kind: 'circuits' | 'bom' | 'labels' | 'terminals') => {
    try {
      const x = await exporters();
      if (kind === 'labels')
        return await saveExport(
          await x.breakerLabelsPdf(project, analysis),
          `${baseName} - ${say(locale, 'étiquettes', 'labels', 'etiketten', 'Etiketten')}.pdf`,
          'pdf',
          'application/pdf',
        );
      if (kind === 'terminals')
        return await saveExport(
          new TextEncoder().encode(x.terminalPlanCsv(project)),
          `${baseName} - ${say(locale, 'plan de bornes', 'terminal plan', 'klemmenplan', 'Klemmenplan')}.csv`,
          'csv',
          'text/csv',
        );
      const csv = kind === 'circuits' ? x.circuitsCsv(project, locale) : x.bomCsv(project, locale);
      const suffix =
        kind === 'circuits'
          ? say(locale, 'circuits', 'circuits', 'kringen', 'Stromkreise')
          : say(locale, 'nomenclature', 'bill of materials', 'materiaallijst', 'Materialliste');
      await saveExport(new TextEncoder().encode(csv), `${baseName} - ${suffix}.csv`, 'csv', 'text/csv');
    } catch (e) {
      setMessage(t('error.export', { msg: (e as Error).message }));
    }
  };

  /** Breaker labels for a label printer (Brother P-touch, Dymo): one strip per rail. */
  const exportTapeLabels = async (tapeMm: number) => {
    try {
      const x = await exporters();
      const strips = labelStrips(project, undefined, 18, analysis);
      if (!strips.length) return setMessage(t('circ.noSingleLine'));
      await saveExport(
        await x.tapeLabelsPdf(strips, { tapeMm, title: project.meta.title }),
        `${baseName} - ${say(locale, 'étiquettes', 'labels', 'etiketten', 'Etiketten')} ${tapeMm} mm.pdf`,
        'pdf',
        'application/pdf',
      );
      setMessage(t('report.tapeHint', { mm: tapeMm, n: strips.length }));
    } catch (e) {
      setMessage(t('error.export', { msg: (e as Error).message }));
    }
  };

  const exportFile = async (kind: 'pdf' | 'pdf+' | 'svg' | 'png' | 'dxf') => {
    try {
      let bytes: Uint8Array;
      let mime: string;
      const name = kind.startsWith('pdf') ? `${baseName}.pdf` : `${baseName} - ${sheet.name}.${kind}`;
      const { projectToPdf, sheetToSvg, svgToPng, sheetToDxf } = await exporters();
      const files = assets.forProject(project);
      if (kind === 'pdf' || kind === 'pdf+') {
        bytes = await projectToPdf(project, undefined, files, {
          circuitList: kind === 'pdf+',
          terminalPlan:
            kind === 'pdf+' && Object.values(project.elements).some((e) => e.symbolId === 'iec.terminal'),
          revisions: kind === 'pdf+' && !!latestRevision(project),
        });
        mime = 'application/pdf';
      } else if (kind === 'dxf') {
        bytes = new TextEncoder().encode(sheetToDxf(project, sheet));
        mime = 'application/dxf';
      } else {
        const svg = sheetToSvg(project, sheet, files);
        if (kind === 'svg') {
          bytes = new TextEncoder().encode(svg);
          mime = 'image/svg+xml';
        } else {
          const { w, h } = paperSize(sheet);
          bytes = await svgToPng(svg, w, h);
          mime = 'image/png';
        }
      }
      await saveExport(bytes, name, kind.startsWith('pdf') ? 'pdf' : kind, mime);
    } catch (e) {
      setMessage(t('error.export', { msg: (e as Error).message }));
    }
  };

  const onPrint = async () => {
    try {
      const { projectToPdf } = await exporters();
      await platform.openPdf(
        await projectToPdf(project, undefined, assets.forProject(project)),
        `${baseName}.pdf`,
      );
    } catch (e) {
      setMessage(t('error.export', { msg: (e as Error).message }));
    }
  };

  // ---------- autosave & recovery ----------
  // Unsaved work is copied on this device (one copy per project) and, when signed in, to the
  // account, so it can be recovered after a crash or a closed tab, here or on another device.
  const account = cloudState.me && !cloudState.offline ? cloud.client : null;
  /** Workspace of the open cloud project, for its invoice series (also offline: see InvoiceDialog). */
  const invoiceWorkspace =
    cloudState.live && cloud.client
      ? {
          id: cloudState.live.teamId,
          name: cloudState.me?.teams.find((x) => x.id === cloudState.live!.teamId)?.name ?? '',
          role: cloudState.live.role,
          client: cloud.client,
        }
      : null;
  const accountRef = useRef(account);
  accountRef.current = account;
  /** Projects autosaved (or restored) in this session: their copies go once they are saved. */
  const touched = useRef(new Set<string>());
  const lastAutosave = useRef<RecoveryEntry | null>(null);
  const uploaded = useRef<RecoveryEntry | null>(null);
  const accountTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Account requests one at a time, in order (an upload never lands after the delete that follows it). */
  const accountQueue = useRef<Promise<unknown>>(Promise.resolve());
  const toAccount = (f: () => Promise<unknown>) => {
    accountQueue.current = accountQueue.current.then(f).catch(() => undefined);
    return accountQueue.current;
  };
  const uploadAutosave = () => {
    if (accountTimer.current) clearTimeout(accountTimer.current);
    accountTimer.current = null;
    const e = lastAutosave.current;
    const a = accountRef.current;
    if (!e || !a || uploaded.current === e) return;
    uploaded.current = e;
    void toAccount(() =>
      a.putAutosave(e.id, e.name, e.savedAt, e.bytes).catch((err: unknown) => {
        if (uploaded.current === e) uploaded.current = null; // try again with the next copy
        throw err;
      }),
    );
  };
  const forgetAutosave = (id: string) => {
    touched.current.delete(id);
    if (lastAutosave.current?.id === id) lastAutosave.current = null;
    setLocalSaves((l) => l.filter((x) => x.id !== id));
    setAccountSaves((l) => l.filter((x) => x.projectId !== id));
    void clearRecovery(id);
    const a = accountRef.current;
    if (a) void toAccount(() => a.deleteAutosave(id));
  };

  useEffect(() => {
    void listRecoveries().then(setLocalSaves);
  }, []);

  // Signed in: the account's copies, and this device's copies made while signed out or offline.
  const accountUser = account ? cloudState.me?.user.id : undefined;
  useEffect(() => {
    if (!accountUser || !account) return setAccountSaves([]);
    let stop = false;
    void toAccount(async () => {
      const remote = await account.autosaves();
      if (stop) return;
      setAccountSaves(remote);
      for (const l of newerOnDevice(await listRecoveries(), remote))
        await account.putAutosave(l.id, l.name, l.savedAt, l.bytes);
    });
    return () => {
      stop = true;
    };
  }, [accountUser]);

  useEffect(() => {
    if (!dirty || live) return;
    const id = setTimeout(() => {
      const entry: RecoveryEntry = {
        id: project.id,
        bytes: serializeProject({ project, assets: assets.forProject(project) }),
        name: baseName,
        savedAt: Date.now(),
      };
      touched.current.add(project.id);
      lastAutosave.current = entry;
      void saveRecovery(entry).then(() => setAutosavedAt(entry.savedAt));
      // To the account at most every ACCOUNT_AUTOSAVE_MS while editing (the latest copy then).
      if (accountRef.current && !accountTimer.current)
        accountTimer.current = setTimeout(uploadAutosave, ACCOUNT_AUTOSAVE_MS);
    }, AUTOSAVE_MS);
    return () => clearTimeout(id);
  }, [project, dirty, baseName]);

  // Leaving the app: send the latest copy now rather than when the timer fires.
  useEffect(() => {
    // The last session is the tab used last: written when it comes to the front, and when it is
    // closed with a change not written yet (closing an untouched tab does not take over).
    const f = () => {
      if (document.visibilityState !== 'hidden') return writeSession.current();
      if (sessionPending.current) writeSession.current();
      uploadAutosave();
    };
    const focus = () => writeSession.current();
    document.addEventListener('visibilitychange', f);
    window.addEventListener('focus', focus);
    return () => {
      document.removeEventListener('visibilitychange', f);
      window.removeEventListener('focus', focus);
    };
  }, []);

  // Saved (to a file, this device's library or the cloud): its autosaves are no longer needed.
  useEffect(() => {
    if (!dirty && touched.current.has(project.id)) forgetAutosave(project.id);
  }, [dirty, project.id]);

  const recoveries = useMemo(
    () => mergeAutosaves(localSaves, accountSaves, project.id),
    [localSaves, accountSaves, project.id],
  );
  const recovery = recoveryDismissed || viewer ? null : (recoveries[recoveryPick] ?? recoveries[0] ?? null);

  useEffect(() => {
    if (!dirty || platform.kind !== 'web') return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, platform.kind]);

  const restore = async () => {
    const c = recovery;
    if (!c || !(await confirmDiscard())) return;
    try {
      const bytes = c.bytes ?? (await cloud.client!.autosave(c.id));
      const r = deserializeProject(bytes);
      load(r.project, null, false, r.assets);
      setSavedProject(createProject()); // restored work is unsaved until saved
      touched.current.add(r.project.id);
      setLocalSaves((l) => l.filter((x) => x.id !== c.id));
      setAccountSaves((l) => l.filter((x) => x.projectId !== c.id));
      setRecoveryPick(0);
    } catch (e) {
      setMessage(t('error.open', { msg: (e as Error).message }));
    }
  };

  // ---------- position plan background ----------
  const importBackground = async () => {
    try {
      const f = await platform.openFile({ extensions: PLAN_EXTENSIONS, description: 'Plan' });
      if (!f) return;
      if (isRoomScanFile(f.name)) {
        try {
          setRoomScan(parseRoomScan(f.bytes));
        } catch (e) {
          if (!(e instanceof RoomScanError)) throw e;
          setMessage(t('scan.notScan'));
        }
        return;
      }
      if (isCadFile(f.name)) {
        try {
          setDxf({ drawing: parseDxf(f.bytes) });
        } catch (e) {
          if (!(e instanceof CadFormatError)) throw e;
          setMessage(
            e.kind === 'dwg'
              ? t('dxf.dwg', { version: e.detail })
              : t(e.kind === 'dxf-binary' ? 'dxf.binary' : 'dxf.notDxf'),
          );
        }
        return;
      }
      setMessage(t('plan.importing'));
      const img = await importPlan(f.name, f.bytes);
      const id = `plan-${newId()}.${img.mime === 'image/png' ? 'png' : 'jpg'}`;
      await assets.add(id, img.bytes, img.mime);
      // Fit the plan into the drawing area (or use its true size for PDF pages at the sheet scale).
      const { w, h } = paperSize(sheet);
      const area = { x: 15, y: 20, w: w - 30, h: h - 60 };
      const ratio = img.heightPx / img.widthPx;
      const width = Math.min(area.w, area.h / ratio);
      editor.history.dispatch({
        label: t('tx.background'),
        ops: [
          {
            op: 'update',
            coll: 'sheets',
            id: sheet.id,
            patch: {
              background: {
                assetId: id,
                mime: img.mime,
                x: area.x,
                y: area.y,
                width,
                height: width * ratio,
                opacity: 0.45,
              },
              planScale: sheet.planScale ?? 50,
            },
          },
        ],
      });
      setMessage(null);
    } catch (e) {
      setMessage(t('error.open', { msg: (e as Error).message }));
    }
  };

  /** LiDAR room scan on an iPhone or iPad (RoomPlan), then the same dialog as a scan file. */
  const scanRooms = async () => {
    try {
      const bytes = await platform.roomScan!.scan();
      if (bytes) setRoomScan(parseRoomScan(bytes));
    } catch (e) {
      setMessage(t('scan.failed', { msg: (e as Error).message }));
    }
  };

  /** A DXF plan or a room scan, placed at its true size at the chosen scale (top left of the drawing area). */
  const importVectorPlan = async (plan: VectorPlan, scale: number) => {
    setDxf(null);
    setRoomScan(null);
    const id = `plan-${newId()}.json`;
    await assets.add(id, serializePlan(plan), VECTOR_PLAN_MIME);
    const { width, height } = planSize(plan);
    editor.history.dispatch({
      label: t('tx.background'),
      ops: [
        {
          op: 'update',
          coll: 'sheets',
          id: sheet.id,
          patch: {
            background: {
              assetId: id,
              mime: VECTOR_PLAN_MIME,
              x: 15,
              y: 20,
              width: width / scale,
              height: height / scale,
              opacity: 0.8,
            },
            planScale: scale,
          },
        },
      ],
    });
    requestAnimationFrame(() => canvas.current?.fitPage());
  };

  // ---------- AI ----------
  const importBoard = (ex: BoardExtraction) => {
    setAiDialog(null);
    const id = newId();
    const base = t('sheetKind.single-line');
    const order = Math.max(...Object.values(project.sheets).map((s) => s.order)) + 1;
    const withSheet = {
      ...project,
      sheets: {
        ...project.sheets,
        [id]: {
          id,
          name: `${base} (IA)`,
          kind: 'single-line' as const,
          paper: 'A3' as const,
          orientation: 'landscape' as const,
          order,
        },
      },
    };
    const tx = extractionToTransaction(withSheet, id, ex, t('tx.aiImport'));
    history.dispatch({
      label: t('tx.aiImport'),
      ops: [{ op: 'add', coll: 'sheets', value: withSheet.sheets[id]! }, ...tx.ops],
    });
    editor.setSheet(id);
    setPanel('circuits');
  };

  const singleLineSheet =
    sheet.kind === 'single-line' ? sheet : sheetsInOrder(project).find((s) => s.kind === 'single-line');
  /** Select items (and switch to their sheet), then zoom to them. */
  const show = (ids: Id[], sheetId: Id | undefined) => {
    const s = sheetId ?? (ids[0] ? project.elements[ids[0]]?.sheetId : undefined);
    if (s) editor.setSheet(s);
    editor.setTool({ kind: 'select' });
    editor.select(
      ids.filter((id) => project.elements[id]?.sheetId === (s ?? editor.sheetId) || project.wires[id]),
    );
    requestAnimationFrame(() => canvas.current?.zoomTo(ids));
  };

  // ---------- diagram commands ----------
  const run = (label: string, ops: Op[], showSheet?: Id) => {
    if (!ops.length) return setMessage(t('cmd.nothing'));
    history.dispatch({ label, ops });
    if (showSheet && history.project.sheets[showSheet]) editor.setSheet(showSheet);
    setMessage(null);
  };
  const generateMultiLine = (fromSheetId?: Id) => {
    const start = fromSheetId ?? singleLineSheet?.id;
    if (!start) return setMessage(t('circ.noSingleLine'));
    // From a continuation sheet, generate for the whole board.
    const from = boardRootSheet(project, analysis, start);
    const r = generateMultiLineOps(project, from, analysis);
    run(t('tx.multiLine'), r.ops, r.sheetId);
    setMessage(t('cmd.multiLineDone', { name: history.project.sheets[r.sheetId]?.name ?? '' }));
  };
  const generateBoardLayout = (fromSheetId?: Id) => {
    const start = fromSheetId ?? singleLineSheet?.id;
    if (!start) return setMessage(t('circ.noSingleLine'));
    const from = boardRootSheet(project, analysis, start);
    const r = generateBoardLayoutOps(project, from, {}, analysis);
    run(t('tx.boardLayout'), r.ops, r.sheetId);
    requestAnimationFrame(() => canvas.current?.fitPage());
  };
  const regenerate = (s: Sheet) =>
    s.source?.kind === 'board-layout'
      ? generateBoardLayout(s.source.fromSheetId)
      : generateMultiLine(s.source?.fromSheetId);
  /** Take the user where they fix a finding the assistant cannot (findingActions.ts). */
  /** Select new elements and open their properties (`show` reads this render's project). */
  const selectNew = (ids: Id[]) => {
    editor.setTool({ kind: 'select' });
    editor.select(ids);
    openSection('selection', () => {
      canvas.current?.zoomTo(ids);
      inspector.current?.focusFirst();
    });
  };
  /** "Read from a photo": one device's or cable's markings, for the value the finding misses. */
  const readPartFor = async (f: Finding) => {
    const file = await platform.openFile({ extensions: Object.keys(PHOTO_MEDIA), description: 'Photo' });
    if (!file) return;
    const small = await prepareAiImage(
      file.bytes,
      PHOTO_MEDIA[file.name.toLowerCase().split('.').pop() ?? ''] ?? 'image/jpeg',
    );
    setMessage(t('ai.thinking'));
    try {
      const part = await ai.readPart(small.bytes, small.mime);
      const c = analysis.circuits.find((x) => f.targets.includes(x.device.id));
      const dev = project.elements[f.targets[0] ?? ''];
      const ops: Op[] = [];
      const read: string[] = [];
      const props = partDeviceProps(part);
      if (dev && f.ruleId !== 'R-CIR-04' && Object.keys(props).length) {
        ops.push({
          op: 'update',
          coll: 'elements',
          id: dev.id,
          patch: { props: { ...dev.props, ...props } },
        });
        read.push(
          Object.entries(props)
            .map(([k, v]) => `${k} ${v}`)
            .join(', '),
        );
      }
      if (c?.circuit && part.cable && f.ruleId === 'R-CIR-04') {
        ops.push({
          op: 'update',
          coll: 'circuits',
          id: c.circuit.id,
          patch: { cable: { ...c.circuit.cable, ...part.cable } },
        });
        read.push(`${part.cable.type} ${part.cable.cores}G${part.cable.sectionMm2}`);
      }
      if (!ops.length) return setMessage(t('checks.photoNone'));
      run(t('checks.goPhoto'), ops);
      setMessage(t('checks.photoRead', { what: read.join(' · ') }));
    } catch (e) {
      setMessage(isAiUnavailable(e) ? t('ai.unavailable') : t('ai.error', { msg: (e as Error).message }));
    }
  };
  // "Report this rule": through the account, when the server has accounts.
  const ruleReporter: RuleReporter | undefined = !cloudState.available
    ? undefined
    : cloudState.me && cloud.client
      ? {
          kind: 'send',
          send: async (f, reason, comment) => {
            await cloud.client!.reportRule({
              ruleId: f.ruleId,
              standard: project.meta.standard ?? 'rgie',
              verified: ruleInfo(f.ruleId, project.meta.standard)?.verified ?? true,
              reason,
              message: pick(f.message, locale).slice(0, 500),
              comment,
              locale,
              app: `${APP_VERSION} ${platform.kind}`,
            });
          },
        }
      : { kind: 'signin', signIn: () => setCloudDialog('cloud') };
  const gotoFinding = (f: Finding, b: FindingButton, pick?: string) => {
    const to = b.to;
    const sheetId = f.sheetId ?? (f.targets[0] ? project.elements[f.targets[0]]?.sheetId : undefined);
    const target = f.targets[0];
    switch (to) {
      case 'renumber':
        return run(t('checks.goRenumber'), renumberOps(project, analysis));
      case 'numberTerminals':
        return run(t('tx.numberTerminals'), numberTerminalsOps(project));
      case 'toA3':
        return (
          sheetId &&
          run(
            t('checks.goA3'),
            [{ op: 'update', coll: 'sheets', id: sheetId, patch: { paper: 'A3' } }],
            sheetId,
          )
        );
      case 'earthSockets':
        return run(t('checks.goEarthSockets'), earthSocketsOps(project));
      case 'rename':
        return (
          b.arg &&
          run(
            t('checks.goRename', { from: b.from ?? '', tag: b.arg }),
            renameTagOps(project, renameTargets(project, f, b), b.arg),
          )
        );
      case 'attach': {
        if (!target || !pick) return;
        const tag = project.elements[target]?.tag || '';
        run(t('checks.goAttach'), attachPointOps(project, target, pick));
        return setMessage(t('checks.attached', { tag, code: pick }));
      }
      case 'drawRoom': {
        if (!target) return;
        const r = roomAroundOps(project, target);
        if (!r.ops.length) return;
        run(t('checks.goDrawRoom'), r.ops, sheetId);
        return selectNew([r.id!]);
      }
      case 'delete':
        return (
          target &&
          run(t('checks.goDelete'), [
            ...Object.values(project.wires)
              .filter((w) => [w.from, w.to].some((x) => x.kind === 'port' && x.elementId === target))
              .map((w): Op => ({ op: 'remove', coll: 'wires', id: w.id })),
            { op: 'remove', coll: 'elements', id: target },
          ])
        );
      case 'spare': {
        const c = analysis.circuits.find((x) => f.targets.includes(x.device.id))?.circuit;
        return (
          c &&
          run(t('checks.goSpare'), [{ op: 'update', coll: 'circuits', id: c.id, patch: { spare: true } }])
        );
      }
      case 'estimateLengths': {
        const r = estimateLengthsOps(project);
        if (!r.ops.length) return setMessage(t('checks.estimateNone'));
        run(t('checks.goEstimate'), r.ops);
        setPanel('circuits');
        return setMessage(t('checks.estimated', { codes: r.codes.join(', ') }));
      }
      case 'quick': {
        const q = quickFix(project, f);
        return q && run(t(QUICK_TX[q.to], { tag: q.arg }), q.ops, sheetId);
      }
      case 'photo':
        return void withAccount('board', () => void readPartFor(f));
      case 'blankingOk':
        return sheetId && run(t('checks.goBlankingOk'), blankingOps(project, sheetId, 'ok'));
      case 'balance': {
        if (!sheetId) return;
        const moves = balancedPhases(project, analysis, sheetId);
        const ops: Op[] = [...moves].map(([id, phase]) => ({
          op: 'update',
          coll: 'elements',
          id,
          patch: { props: { ...project.elements[id]!.props, phase } },
        }));
        if (!ops.length) return setMessage(t('checks.balanceNone'));
        run(t('checks.goBalance'), ops, sheetId);
        return setMessage(t('checks.balanced', { n: ops.length }));
      }
      case 'addNorth': {
        if (!sheetId) return;
        const r = northArrowOps(project, sheetId);
        run(t('checks.goAddNorth'), r.ops, sheetId);
        editor.setTool({ kind: 'select' });
        editor.select([r.id]);
        return openSection('selection', () => {
          canvas.current?.zoomTo([r.id]);
          inspector.current?.focusFirst();
        });
      }
      case 'drawRoutes': {
        if (!sheetId) return;
        const ops = cableRoutesOps(project, sheetId, analysis);
        if (!ops.length) return setMessage(t('checks.routesNone'));
        run(t('checks.goDrawRoutes'), ops, sheetId);
        return setMessage(t('checks.routesDrawn', { n: ops.length }));
      }
      default:
        break;
    }
    if (to === 'projectInfo') return setShowInfo('missing');
    if (to === 'measurements') return setAfterDialog('measurements');
    if (to === 'survey') return generateFromSurveyCmd();
    if (to === 'addEarth') {
      const r = earthElectrodeOps(project, singleLineSheet?.id);
      if (!r.ops.length) return setMessage(t('circ.noSingleLine'));
      run(t('checks.goAddEarth'), r.ops, singleLineSheet?.id);
      // Select the new electrode (`show` would look it up in this render's project).
      editor.setTool({ kind: 'select' });
      editor.select([r.id!]);
      return openSection('selection', () => {
        canvas.current?.zoomTo([r.id!]);
        inspector.current?.focusFirst();
      });
    }
    if (to === 'newPlan') {
      const id = newId();
      history.dispatch({
        label: t('tx.sheetAdd'),
        ops: [
          {
            op: 'add',
            coll: 'sheets',
            value: {
              id,
              name: t('sheetKind.position-plan'),
              kind: 'position-plan',
              paper: 'A3',
              orientation: 'landscape',
              planScale: 50,
              order: Math.max(...Object.values(project.sheets).map((s) => s.order)) + 1,
            },
          },
        ],
      });
      return editor.setSheet(id);
    }
    if (to === 'circuits') {
      if (f.targets.length) show(f.targets, sheetId);
      return setPanel('circuits');
    }
    if (to === 'update') {
      const s = sheetId ? project.sheets[sheetId] : undefined;
      return s && regenerate(s);
    }
    show(f.targets, sheetId);
    if (to === 'properties') openSection('selection', () => inspector.current?.focusFirst());
    if (to === 'sheet') openSection('sheet');
  };
  const insertTemplate = (tpl: InsertTemplate) => {
    const r = tpl.ops(project, singleLineSheet?.id);
    if (!r.ops.length) return setMessage(t('circ.noSingleLine'));
    run(t('tx.template', { name: pick(tpl.name, locale) }), r.ops, r.sheetId);
    if (tpl.kind === 'board') setPanel('circuits');
  };
  const newFromTemplate = async (tpl: ProjectTemplate) => {
    setShowTemplates(false);
    if (!(await confirmDiscard())) return;
    forgetAutosave(history.project.id);
    load(tpl.create(locale), null, true);
  };
  const createFromSketch = (sketched: BoardSpec) => {
    setAiDialog(null);
    // The diagram needs the earth electrode: drawn, with the value when the sketch gives one.
    const board = sketched.earth || !earthNeeds(project).required ? sketched : { ...sketched, earth: {} };
    const id = newId();
    const order = Math.max(...Object.values(project.sheets).map((s) => s.order)) + 1;
    const sheetOp: Op = {
      op: 'add',
      coll: 'sheets',
      value: {
        id,
        name: `${t('sheetKind.single-line')} (${tr(project.meta.locale, 'esquisse', 'sketch', 'schets', 'Skizze')})`,
        kind: 'single-line',
        paper: 'A4',
        orientation: 'landscape',
        order,
      },
    };
    const withSheet = applyTransaction(project, { label: '', ops: [sheetOp] }).project;
    run(t('tx.sketch'), [sheetOp, ...generateBoardOps(withSheet, id, board)], id);
    setPanel('circuits');
    requestAnimationFrame(() => canvas.current?.fitPage());
  };
  /** A finding sent to the assistant ("Fix with the assistant"). */
  // A finding sent to the assistant; it belongs to the project it was found in.
  const [assistRequest, setAssistRequest] = useState<{
    text: string;
    n: number;
    projectId: Id;
    fix?: FixTarget;
  } | null>(null);
  const comparing = compareRev ? project.revisions[compareRev] : undefined;
  const comparingTo = comparing && compareTo ? project.revisions[compareTo] : undefined;
  // The assistant's proposal under the pointer, shown on the canvas but not applied.
  const previewed = editor.preview;
  const diff = useMemo(
    () =>
      comparing
        ? diffProjects(comparing.snapshot, comparingTo && previewed ? previewed : project)
        : previewed
          ? diffProjects(project, previewed)
          : undefined,
    [comparing, comparingTo, project, previewed],
  );
  /** Compare a revision with a later one (drawn read-only) or with now. */
  const compare = (from: Id | null, to: Id | null) => {
    setCompareRev(from);
    setCompareTo(to);
    const rev = to ? project.revisions[to] : undefined;
    editor.setPreview(from && rev ? restoreRevisionOps(project, rev) : null);
  };
  // An edit drops the preview of the later revision: the comparison ends with it.
  useEffect(() => {
    if (compareTo && !previewed) {
      setCompareRev(null);
      setCompareTo(null);
    }
  }, [compareTo, previewed]);
  const changesLabel = () =>
    comparing
      ? t('rev.listTitle', { from: comparing.index, to: comparingTo ? comparingTo.index : t('rev.now') })
      : '';
  const showChanges = async () => {
    if (!comparing) return;
    setChanges({ report: null });
    const x = await exporters();
    const after = comparingTo
      ? ({ ...comparingTo.snapshot, revisions: project.revisions } as Project)
      : project;
    setChanges({ report: x.describeChanges(comparing.snapshot, after, locale) });
  };
  const exportChanges = async (from: Id, to: Id | null) => {
    const a = project.revisions[from];
    if (!a) return;
    try {
      const x = await exporters();
      await saveExport(
        await x.changesPdf(
          project,
          a,
          to ? project.revisions[to] : undefined,
          assets.forProject(project),
          locale,
        ),
        `${baseName} - ${t('rev.file')} ${a.index}-${to ? project.revisions[to]?.index : t('rev.now')}.pdf`,
        'pdf',
        'application/pdf',
      );
    } catch (e) {
      setMessage(t('error.export', { msg: (e as Error).message }));
    }
  };
  /** Show a proposal: on the current sheet if it changes it, else on the sheet it changes. */
  const previewProposal = (ops: Op[] | null, target: Id | undefined) => {
    // Comparing two revisions: the later one is the preview, leave it.
    if (comparingTo) return;
    if (!ops?.length || comparing) return editor.setPreview(null);
    const after = applyTransaction(project, { label: '', ops }).project;
    const d = diffProjects(project, after);
    const touched = new Set<Id>();
    for (const id of [...d.added, ...d.changed]) {
      const s = after.elements[id]?.sheetId ?? after.wires[id]?.sheetId;
      if (s) touched.add(s);
    }
    for (const x of [...d.removedElements, ...d.removedWires]) touched.add(x.sheetId);
    const current = editor.preview ? undefined : editor.sheetId;
    const sheetId =
      current && touched.has(current) ? current : target && touched.has(target) ? target : [...touched][0];
    editor.setPreview(ops, sheetId);
  };
  // Opt-in usage statistics: sent to the server the app talks to, when the user turned them on.
  const localeRef = useRef(locale);
  localeRef.current = locale;
  useEffect(
    () =>
      startUsage({
        platform,
        baseUrl: cloud.client?.baseUrl,
        version: APP_VERSION,
        locale: () => localeRef.current,
      }),
    [platform, cloud.client?.baseUrl],
  );
  // Automatic backup export: a .tablo copy in the chosen folder while the project changes.
  const backup = useAutoBackup(platform, project, () =>
    serializeProject({ project: history.project, assets: assets.forProject(history.project) }),
  );
  /** Pins on a sheet: the review link's, or the project's remarks that were pinned. */
  const pinsOn = useCallback(
    (sheetId: string) => {
      if (viewer?.review)
        return viewer.review.pins.flatMap((p, i) =>
          p.sheetId === sheetId
            ? [{ x: p.x, y: p.y, label: String(i + 1), done: p.status === 'imported' }]
            : [],
        );
      const out: Array<{ x: number; y: number; label: string; done?: boolean }> = [];
      for (const insp of Object.values(project.inspections ?? {})) {
        const rs = Object.values(project.remarks ?? {})
          .filter((r) => r.inspectionId === insp.id)
          .sort((a, b) => a.order - b.order);
        rs.forEach((r, i) => {
          if (r.pin?.sheetId === sheetId)
            out.push({ x: r.pin.x, y: r.pin.y, label: String(i + 1), done: r.status === 'fixed' });
        });
      }
      return out;
    },
    [viewer, project],
  );
  const pickPin = useCallback(
    (p: { x: number; y: number }) => {
      setPicking(false);
      setPinDraft({ sheetId: editor.sheetId, x: p.x, y: p.y, ref: nearestTag(project, editor.sheetId, p) });
    },
    [editor, project],
  );
  useEffect(() => {
    if (!picking) return;
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setPicking(false);
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [picking]);
  // The installer: remarks pinned online on this cloud project, waiting to be taken over.
  useEffect(() => {
    if (afterDialog !== 'inspections' || !live?.projectId || !account) return;
    void account
      .projectPins(live.projectId)
      .then((pins) => setPendingPins(pins.filter((p) => p.status === 'new')))
      .catch(() => setPendingPins([]));
  }, [afterDialog, live?.projectId, account]);
  const importPins = async () => {
    if (!pendingPins.length || !live?.projectId || !account) return;
    const { ops } = pinsToOps(
      pendingPins,
      t('inspection.pinsReference'),
      new Date().toISOString().slice(0, 10),
    );
    history.dispatch({ label: t('inspection.txPins'), ops });
    try {
      await account.pinsImported(
        live.projectId,
        pendingPins.map((p) => p.id),
      );
      setPendingPins([]);
    } catch (e) {
      setMessage((e as Error).message);
    }
  };
  const createPv = (spec: Omit<PvSpec, 'sheetName' | 'circuitLabel'>) => {
    setShowPv(false);
    const r = pvInstallationOps(project, {
      ...spec,
      sheetName: t('pv.sheetName'),
      circuitLabel: t('pv.circuitLabel'),
    });
    history.dispatch({ label: t('pv.tx'), ops: r.ops });
    editor.setSheet(r.dcSheetId);
    requestAnimationFrame(() => canvas.current?.fitPage());
  };
  const exportPvLabels = async () => {
    const x = await exporters();
    if (!x.pvLabels(project).length) return setMessage(t('pv.noLabels'));
    await saveExport(
      await x.pvLabelsPdf(project),
      `${baseName} - ${t('pv.labelsFile')}.pdf`,
      'pdf',
      'application/pdf',
    );
  };
  const createRevision = (description: string) => {
    const op = createRevisionOp(project, { description });
    history.dispatch({
      label: t('tx.revision', { index: op.op === 'add' && op.coll === 'revisions' ? op.value.index : '' }),
      ops: [op],
    });
  };
  const restoreRevision = async (id: Id) => {
    const rev = project.revisions[id];
    if (!rev) return;
    if (
      !(await ask({
        title: t('rev.restore'),
        body: t('rev.restoreConfirm', { index: rev.index }),
        confirm: t('rev.restore'),
      }))
    )
      return;
    setShowRevisions(false);
    compare(null, null);
    history.dispatch({
      label: t('tx.restoreRevision', { index: rev.index }),
      ops: restoreRevisionOps(project, rev),
    });
    const first = sheetsInOrder(history.project)[0];
    if (first) editor.setSheet(first.id);
  };
  // ---------- user symbols, macros, libraries ----------
  const saveSymbol = (s: CustomSymbol) => {
    setSymbolEditor(null);
    const exists = !!project.symbols[s.id];
    const { id, ...rest } = s;
    history.dispatch({
      label: t(exists ? 'tx.editSymbol' : 'tx.newSymbol', { name: s.name[cl] || s.name.fr }),
      ops: [
        exists
          ? { op: 'update', coll: 'symbols', id, patch: rest }
          : { op: 'add', coll: 'symbols', value: s },
      ],
    });
    if (!exists) editor.setTool({ kind: 'place', symbolId: s.id });
  };
  const saveMacro = (name: string) => {
    setAskMacroName(false);
    saveMacros([...macros, makeMacro(project, editor.selection, name, newId())]);
    setMessage(t('macro.saved', { name }));
  };
  const insertMacro = (m: Macro) => editor.insertMacro(m, canvas.current?.viewCenter());
  const library = async (action: 'export-symbols' | 'import' | 'export-macros') => {
    try {
      if (action === 'import') {
        const f = await platform.openFile({ extensions: ['json'], description: 'Tablo' });
        if (!f) return;
        const v: unknown = JSON.parse(new TextDecoder().decode(f.bytes));
        if (isSymbolLibraryFile(v)) {
          const ops = v.symbols
            .filter((s) => !project.symbols[s.id])
            .map((s) => ({ op: 'add', coll: 'symbols', value: s }) as Op);
          if (ops.length) history.dispatch({ label: t('tx.importSymbols'), ops });
          setMessage(t('lib.imported', { n: ops.length }));
        } else if (isMacroLibraryFile(v)) {
          const have = new Set(macros.map((m) => m.id));
          const add = v.macros.filter((m) => !have.has(m.id));
          saveMacros([...macros, ...add]);
          setMessage(t('lib.imported', { n: add.length }));
        } else setMessage(t('lib.notLibrary'));
        return;
      }
      const data =
        action === 'export-symbols'
          ? { app: 'tablo', kind: 'symbols', symbols: Object.values(project.symbols) }
          : { app: 'tablo', kind: 'macros', macros };
      await saveExport(
        new TextEncoder().encode(JSON.stringify(data, null, 1)),
        `${action === 'export-symbols' ? t('lib.symbolsFile') : t('lib.macrosFile')}.json`,
        'json',
        'application/json',
      );
    } catch (e) {
      setMessage(t('error.open', { msg: (e as Error).message }));
    }
  };
  const staleSheets = useMemo(() => staleMultiLines(project, analysis), [project, analysis]);
  const generatedFrom = sheet.source ? project.sheets[sheet.source.fromSheetId] : undefined;
  const isStale = staleSheets.some((s) => s.id === sheet.id);

  // ---------- cloud ----------
  useEffect(() => {
    cloud.onRejected = (label) => setMessage(t('cloud.rejected', { label }));
  }, [cloud, t]);
  /** Settles once the server and the saved account were checked (cloud.init). */
  const cloudReady = useRef<Promise<void> | null>(null);
  useEffect(() => {
    const m = /^#\/(view|review)\/([\w-]+)/.exec(location.hash);
    const view = m?.[2];
    cloudReady.current = cloud.init();
    void cloudReady.current.then(async () => {
      if (!view || !cloud.client) return;
      try {
        const anon = new CloudClient(cloud.client.baseUrl);
        const r = await anon.shared(view);
        const files: Record<string, Uint8Array> = {};
        for (const s of Object.values(r.project.sheets)) {
          const id = s.background?.assetId;
          if (id)
            files[id] = (await anon.sharedAsset(view, id).catch(() => ({ bytes: new Uint8Array() }))).bytes;
        }
        setShowWelcome(false);
        load(r.project, null, true, files);
        history.readOnly = true;
        setViewer({
          revision: r.revision,
          ...(r.kind === 'review' ? { review: { token: view, pins: r.pins ?? [] } } : {}),
        });
      } catch (e) {
        setMessage(t('viewer.error', { msg: (e as Error).message }));
      }
    });
  }, [cloud]);

  // ---------- AI: signed-in accounts only ----------
  /** The AI feature a signed-out person reached for: shows the sign-in prompt. */
  const [aiGate, setAiGate] = useState<AiFeature | null>(null);
  /** What to run once they have signed in from that prompt. */
  const afterSignIn = useRef<(() => void) | null>(null);
  /** Run an AI feature when signed in; otherwise show the sign-in prompt with its demo. */
  const withAccount = useCallback(
    async (feature: AiFeature, run: () => void) => {
      // Just after launch the saved account may not be checked yet.
      if (!cloud.state.me && cloudReady.current) await cloudReady.current;
      if (cloud.state.me) return run();
      afterSignIn.current = run;
      setAiGate(feature);
    },
    [cloud],
  );
  useEffect(() => {
    if (!cloudState.me || !afterSignIn.current) return;
    const run = afterSignIn.current;
    afterSignIn.current = null;
    setAiGate(null);
    setCloudDialog(null);
    run();
  }, [cloudState.me]);
  const aiGateNode = aiGate && (
    <AiSignInPrompt
      feature={aiGate}
      t={t}
      locale={locale}
      onSignIn={() => {
        setAiGate(null);
        setCloudDialog('cloud');
      }}
      onClose={() => {
        afterSignIn.current = null;
        setAiGate(null);
      }}
    />
  );
  useEffect(() => {
    const team = cloudState.team;
    if (!team || !cloud.client) return setTeamItems([]);
    void cloud.client
      .library(team.id)
      .then((items) => setTeamItems(items.filter((i) => i.kind !== 'template')))
      .catch(() => setTeamItems([]));
  }, [cloudState.team?.id, cloud]);

  // The client and site records: remember the open project's client, address and inspections
  // on this device (a few seconds after the last change).
  useEffect(() => {
    if (viewer) return;
    const id = setTimeout(
      () =>
        rememberSite(project, {
          where: cloudState.live?.projectId === project.id ? 'cloud' : 'device',
          ...(cloudState.live?.projectId === project.id ? { teamId: cloudState.live.teamId } : {}),
          ...(file?.name ? { file: file.name } : {}),
        }),
      2000,
    );
    return () => clearTimeout(id);
  }, [
    project.meta,
    project.inspections,
    project.remarks,
    project.id,
    cloudState.live?.projectId,
    file?.name,
    viewer,
  ]);

  /** `afterUpload`: the open project was just uploaded, nothing to discard. */
  const openCloud = async (id: string, afterUpload = false) => {
    if (!afterUpload && !(await confirmDiscard())) return;
    try {
      setCloudDialog(null);
      setViewer(null);
      const p = await cloud.open(id, history, assets);
      setLibKey(null); // a cloud project is kept on the device as its cloud copy
      editor.setSheet(sheetsInOrder(p)[0]!.id);
      editor.select([]);
      editor.setTool({ kind: 'select' });
      setFile(null);
      setSavedProject(p);
      requestAnimationFrame(() => canvas.current?.fitPage());
    } catch (e) {
      setMessage(t('cloud.error', { msg: (e as Error).message }));
    }
  };
  const uploadToCloud = async () => {
    const files = assets.forProject(project);
    const mimes = Object.fromEntries(assetRefs(project).map((r) => [r.id, r.mime]));
    const id = await cloud.upload(project, files, mimes);
    // Its files are on the server and stay on this device for working offline.
    for (const [aid, bytes] of Object.entries(files))
      await putStoredAsset(cloudKey(id), aid, {
        bytes,
        mime: mimes[aid] ?? 'application/octet-stream',
        uploaded: true,
      });
    setSavedProject(project); // nothing to lose: it is in the cloud now
    await openCloud(id, true);
    setMessage(t('cloud.uploaded', { name: cloud.state.team?.name ?? '' }));
  };
  // ---------- site survey: projects kept on this device ----------
  const openKey = live ? cloudKey(live.projectId) : libKey;
  const libSave = useRef<() => void>(() => undefined);
  useEffect(() => {
    if (!libKey || live || viewer) {
      libSave.current = () => undefined;
      return;
    }
    const save = () => {
      const p = history.project;
      void saveLibraryEntry({
        key: libKey,
        kind: 'local',
        title: p.meta.title,
        updatedAt: Date.now(),
        project: p,
      }).then((ok) => {
        if (ok && history.project === p) setSavedProject(p);
        if (!ok) setMessage(t('error.save', { msg: 'IndexedDB' }));
      });
      for (const r of assetRefs(p)) {
        const b = assets.bytes.get(r.id);
        if (!b || storedIds.current.has(r.id)) continue;
        storedIds.current.add(r.id);
        void putStoredAsset(libKey, r.id, { bytes: b, mime: r.mime, uploaded: true });
      }
    };
    libSave.current = save;
    const id = setTimeout(save, 600);
    return () => clearTimeout(id);
  }, [project, libKey, live, viewer]);
  // Phones stop apps in the background without warning: save as soon as the app is hidden.
  useEffect(() => {
    const f = () => document.visibilityState === 'hidden' && libSave.current();
    document.addEventListener('visibilitychange', f);
    window.addEventListener('pagehide', f);
    return () => {
      document.removeEventListener('visibilitychange', f);
      window.removeEventListener('pagehide', f);
    };
  }, []);

  const keepOnDevice = async (p: Project = history.project) => {
    storedIds.current = new Set();
    const ok = await saveLibraryEntry({
      key: p.id,
      kind: 'local',
      title: p.meta.title,
      updatedAt: Date.now(),
      project: p,
    });
    if (!ok) return setMessage(t('error.save', { msg: 'IndexedDB' }));
    setLibKey(p.id);
    setFile(null);
    setSavedProject(p);
    void persistStorage();
  };
  const newSurvey = async () => {
    if (!(await confirmDiscard())) return;
    const date = new Date().toLocaleDateString(localeTag(locale));
    let p = createProject({ locale, title: t('survey.newTitle', { date }) });
    p = applyTransaction(p, { label: '', ops: [addBoardOp(p, { name: t('survey.mainBoard') })] }).project;
    load(p);
    await keepOnDevice(p);
    goSurvey({ screen: 'project', tab: 'rooms' });
  };
  const openEntry = async (e: LibraryEntry) => {
    const toProject = () => goSurvey({ screen: 'project', tab: 'rooms' });
    if (e.key === openKey) return toProject();
    if (e.cloud) {
      await openCloud(e.cloud.projectId);
      if (cloud.state.live) toProject();
      return;
    }
    if (!(await confirmDiscard())) return;
    await loadEntry(e);
    toProject();
  };
  const loadEntry = async (e: LibraryEntry) => {
    const stored = await storedAssets(e.key);
    const p = migrate(e.project as unknown as Record<string, unknown>) as unknown as Project;
    load(p, null, true, Object.fromEntries(Object.entries(stored).map(([k, v]) => [k, v.bytes])));
    setLibKey(e.key);
    storedIds.current = new Set(Object.keys(stored));
  };

  // ---------- last session: the project open when the app was closed opens again ----------
  const [sessionReady, setSessionReady] = useState(false);
  /** The empty project a tab starts with: never remembered (it would replace the real one). */
  const [startProject] = useState(() => history.project);
  useEffect(() => {
    if (/^#\/view\//.test(location.hash)) return; // a shared link opens that project instead
    void (async () => {
      try {
        const s = await loadSession();
        // Still open in another tab: this one starts with a new project instead.
        const held = await navigator.locks
          ?.query()
          .then((q) => q.held?.some((l) => l.name === openLock(s?.kind ?? '', s?.ref ?? '')))
          .catch(() => false);
        if (held) return;
        if (s?.kind === 'cloud') {
          await cloudReady.current;
          if (cloud.state.me) await openCloud(s.ref, true);
        } else if (s?.kind === 'library') {
          const e = await loadLibraryEntry(s.ref);
          if (e?.cloud) {
            await cloudReady.current;
            if (cloud.state.me) await openCloud(e.cloud.projectId, true);
          } else if (e) await loadEntry(e);
        } else if (s?.kind === 'file' && s.bytes) {
          const r = deserializeProject(s.bytes);
          load(r.project, s.file ?? null, !s.unsaved, r.assets);
          if (s.unsaved) {
            setSavedProject(createProject()); // still not saved to its file
            touched.current.add(r.project.id);
          }
        }
      } catch {
        /* nothing to reopen: start with a new project */
      } finally {
        setSessionReady(true);
      }
    })();
  }, []);
  // While a project is open here, other tabs do not reopen it.
  const openRef: [SessionEntry['kind'], string] = live
    ? ['cloud', live.projectId]
    : libKey
      ? ['library', libKey]
      : ['file', project.id];
  const openName = openLock(...openRef);
  useEffect(() => {
    if (!navigator.locks || viewer) return;
    let release = () => undefined as void;
    const held = new Promise<void>((r) => (release = r));
    void navigator.locks.request(openName, () => held).catch(() => undefined);
    return () => release();
  }, [openName, viewer]);
  // Remember the open project (only once the last one was reopened, not to overwrite it first).
  const writeSession = useRef<() => void>(() => undefined);
  /** A change not yet written (the timer is running). */
  const sessionPending = useRef(false);
  useEffect(() => {
    if (!sessionReady || viewer) {
      writeSession.current = () => undefined;
      return;
    }
    const write = () => {
      const p = history.project;
      if (p === startProject) return;
      const e: SessionEntry = live
        ? { kind: 'cloud', ref: live.projectId, savedAt: Date.now() }
        : libKey
          ? { kind: 'library', ref: libKey, savedAt: Date.now() }
          : {
              kind: 'file',
              ref: p.id,
              bytes: serializeProject({ project: p, assets: assets.forProject(p) }),
              file,
              unsaved: p !== savedProject,
              savedAt: Date.now(),
            };
      sessionPending.current = false;
      void saveSession(e);
    };
    writeSession.current = write;
    sessionPending.current = true;
    const id = setTimeout(write, live || libKey ? 0 : 1000);
    return () => clearTimeout(id);
  }, [sessionReady, viewer, project, savedProject, file, libKey, live?.projectId]);
  const removeEntry = async (e: LibraryEntry) => {
    if (e.key === libKey) setLibKey(null);
    await deleteLibraryEntry(e.key);
  };
  const openEditor = (sheetId?: Id) => {
    if (sheetId && project.sheets[sheetId]) editor.setSheet(sheetId);
    window.history.pushState(null, '', location.pathname + location.search);
    setHash('');
    requestAnimationFrame(() => canvas.current?.fitPage());
  };
  const generateFromSurveyCmd = () => {
    const r = generateFromSurvey(project, SURVEY_LABELS[project.meta.locale]);
    history.dispatch({ label: t('survey.generate'), ops: r.ops });
    if (r.sheetIds[0]) editor.setSheet(r.sheetIds[0]);
    setMessage(t('survey.generated', { n: r.sheetIds.length }));
    requestAnimationFrame(() => canvas.current?.fitPage());
  };
  useEffect(() => {
    if (location.hash === '#/survey/new') void newSurvey();
  }, []);
  // Survey screens show messages as a toast that goes away by itself.
  useEffect(() => {
    if (!message || !hash.startsWith('#/survey')) return;
    const id = setTimeout(() => setMessage(null), 4500);
    return () => clearTimeout(id);
  }, [message, hash]);
  const surveyStatus: SurveyStatus = live
    ? !live.online
      ? {
          label: live.pending
            ? `${t('survey.statusOffline')} · ${t('survey.statusPending', { n: live.pending })}`
            : t('survey.statusOffline'),
          tone: 'warn',
        }
      : live.pending
        ? { label: t('survey.statusPending', { n: live.pending }), tone: 'muted' }
        : live.pendingFiles
          ? { label: t('survey.statusFiles', { n: live.pendingFiles }), tone: 'muted' }
          : { label: t('survey.statusSynced'), tone: 'ok' }
    : libKey
      ? { label: t('survey.statusSaved'), tone: dirty ? 'muted' : 'ok' }
      : file
        ? {
            label: dirty ? t('status.unsaved') : t('survey.statusFile', { name: file.name }),
            tone: dirty ? 'warn' : 'muted',
          }
        : { label: t('status.unsaved'), tone: 'warn' };

  const handover = async (link: { url: string; revision: string | null } | null) => {
    const { handoverPdf } = await exporters();
    await saveExport(
      // The QR code opens the homeowner's page (rooms per breaker, what to do when an RCD
      // trips), which links on to the drawings.
      await handoverPdf(
        project,
        assets.forProject(project),
        link && { ...link, url: link.url.replace('/#/view/', '/#/home/') },
      ),
      `${baseName} - ${say(locale, 'dossier de remise', 'handover', 'overdrachtsdossier', 'Übergabeakte')}.pdf`,
      'pdf',
      'application/pdf',
    );
  };
  const exportMeasurementsCsv = async () => {
    const { measurementsCsv } = await exporters();
    await saveExport(
      new TextEncoder().encode(measurementsCsv(project, locale)),
      `${baseName} - ${t('measure.file')}.csv`,
      'csv',
      'text/csv',
    );
  };
  const exportReinspection = async (inspectionId: Id) => {
    try {
      const { reinspectionPdf } = await exporters();
      await saveExport(
        await reinspectionPdf(project, inspectionId, assets.forProject(project)),
        `${baseName} - ${t('inspection.packFile')}.pdf`,
        'pdf',
        'application/pdf',
      );
    } catch (e) {
      setMessage(t('error.export', { msg: (e as Error).message }));
    }
  };
  const insertTeamItem = (item: LibraryItem) => {
    if (item.kind === 'macro') editor.insertMacro(item.data as Macro, canvas.current?.viewCenter());
    else if (item.kind === 'symbol') {
      const s = item.data as CustomSymbol;
      if (!project.symbols[s.id])
        history.dispatch({
          label: t('tx.newSymbol', { name: item.name }),
          ops: [{ op: 'add', coll: 'symbols', value: s }],
        });
      editor.setTool({ kind: 'place', symbolId: s.id });
    }
  };
  const shareToTeam = async () => {
    const team = cloud.state.team;
    if (!team || !cloud.client) return;
    try {
      for (const s of Object.values(project.symbols))
        await cloud.client.putLibrary(team.id, 'symbol', s.id, s.name[cl] || s.name.fr, s);
      for (const m of macros) await cloud.client.putLibrary(team.id, 'macro', m.id, m.name, m);
      setTeamItems((await cloud.client.library(team.id)).filter((i) => i.kind !== 'template'));
      setMessage(t('cloud.sharedToTeam', { name: team.name }));
    } catch (e) {
      setMessage(t('cloud.error', { msg: (e as Error).message }));
    }
  };

  // ---------- benchmark & test hooks ----------
  const runBenchmark = async (count: number, durationMs = 4000) => {
    load(generateBenchmarkProject(count, cl));
    setMessage(t('bench.running', { n: count.toLocaleString(locale) }));
    await new Promise((r) => setTimeout(r, 300));
    const res = await canvas.current!.benchmark(durationMs);
    setMessage(
      t('bench.result', { n: count.toLocaleString(locale), fps: res.fps, avg: res.avgMs, p95: res.p95Ms }),
    );
    return { ...res, elements: count };
  };
  useEffect(() => {
    window.__elec = {
      benchmark: runBenchmark,
      project: () => history.project,
      editor,
      load: (p) => load(p),
      exportPdf: async () => (await exporters()).projectToPdf(history.project),
      toClient: (x, y) => canvas.current!.toClient(x, y),
      findings: () => checkProject(history.project),
      assetIds: () => Object.keys(assets.forProject(history.project)),
      cloud: () => cloud.state,
      peers: () => cloud.peerCursors(editor.sheetId),
    };
  });

  // ---------- guided tutorial ----------
  const panelRef = useRef(panel);
  panelRef.current = panel;
  const tourApp = useMemo<TourApp>(
    () => ({
      editor,
      toClient: (x, y) => canvas.current?.toClient(x, y) ?? { x, y },
      fitPage: () => canvas.current?.fitPage(),
      zoomToArea: (b) => canvas.current?.zoomToArea(b),
      panel: () => panelRef.current,
      setPanel,
      sidebar: () => sideRef.current,
      setSidebar: (st) => setSide(st),
      paletteFolded: () => palFoldedRef.current,
      setPaletteFolded: (f) => setPalFolded(f),
      closeDialogs: () => {
        setShowInfo(false);
        setShowTemplates(false);
        setShowRevisions(false);
        setShowAbout(false);
        setShowQuote(false);
        setAfterDialog(null);
        setAiDialog(null);
      },
    }),
    [editor, setSide],
  );

  // ---------- keyboard ----------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (document.querySelector('.backdrop, .survey')) return; // a dialog or the survey screens
      const typing = (e.target as HTMLElement).closest('input, textarea, select, [contenteditable]');
      const mod = e.metaKey || e.ctrlKey;
      const k = e.key.toLowerCase();
      if (mod && k === 's') {
        e.preventDefault();
        void onSave(e.shiftKey);
        return;
      }
      if (mod && k === 'o') {
        e.preventDefault();
        void onOpen();
        return;
      }
      if (mod && k === 'p') {
        e.preventDefault();
        void onPrint();
        return;
      }
      if (typing) return;
      if (mod && k === 'z') {
        e.preventDefault();
        if (e.shiftKey) history.redo();
        else history.undo();
      } else if (mod && k === 'y') {
        e.preventDefault();
        history.redo();
      } else if (mod && k === 'a') {
        e.preventDefault();
        editor.selectAll();
      } else if (mod && k === 'd') {
        e.preventDefault();
        editor.duplicate();
      } else if (mod) {
        return; // copy/cut/paste handled by clipboard events
      } else if (e.key === 'Escape') editor.cancel();
      else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        if (editor.isWiring) editor.undoWirePoint();
        else editor.deleteSelection();
      } else if (k === 'v') editor.setTool({ kind: 'select' });
      else if (k === 'w') editor.setTool({ kind: 'wire' });
      else if (k === 'p' && sheet.kind === 'position-plan') editor.setTool({ kind: 'room' });
      else if (k === 't') editor.setTool({ kind: 'place', symbolId: 'annot.text' });
      else if (k === 'r') editor.rotate();
      else if (k === 'm') editor.mirror();
      else if (k === 'f') canvas.current?.fit();
      else if (e.key.startsWith('Arrow') && editor.selection.size) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 2.5;
        const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[
          e.key
        ];
        if (d) editor.nudge(d[0]!, d[1]!);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // ---------- clipboard (system clipboard, works across windows) ----------
  useEffect(() => {
    // In the survey screens the drawing is hidden: leave the clipboard to the page.
    const typing = () =>
      !!document.activeElement?.closest('input, textarea, select') || !!document.querySelector('.survey');
    const onCopy = (e: ClipboardEvent) => {
      if (typing() || !editor.selection.size) return;
      const data = e.type === 'cut' ? editor.cut() : editor.copy();
      if (data && e.clipboardData) {
        e.clipboardData.setData('text/plain', JSON.stringify(data));
        e.preventDefault();
      }
    };
    const onPaste = (e: ClipboardEvent) => {
      if (typing()) return;
      let data = null;
      try {
        const parsed = JSON.parse(e.clipboardData?.getData('text/plain') ?? '');
        if (isClipboardData(parsed)) data = parsed;
      } catch {
        /* not ours: fall back to the internal clipboard */
      }
      editor.paste(data ?? undefined);
      e.preventDefault();
    };
    document.addEventListener('copy', onCopy);
    document.addEventListener('cut', onCopy);
    document.addEventListener('paste', onPaste);
    return () => {
      document.removeEventListener('copy', onCopy);
      document.removeEventListener('cut', onCopy);
      document.removeEventListener('paste', onPaste);
    };
  }, [editor]);

  const tool = editor.tool;
  const nElements = Object.values(project.elements).filter((e) => e.sheetId === sheet.id).length;
  const nWires = Object.values(project.wires).filter((w) => w.sheetId === sheet.id).length;
  const hint =
    tool.kind === 'wire'
      ? t('hint.wire')
      : tool.kind === 'place'
        ? t('hint.place')
        : tool.kind === 'room'
          ? t('hint.room')
          : tool.kind === 'calibrate'
            ? t('hint.calibrate')
            : t('hint.canvas');
  const isPlan = sheet.kind === 'position-plan';
  const time = (ms: number) =>
    new Date(ms).toLocaleTimeString(localeTag(locale), {
      hour: '2-digit',
      minute: '2-digit',
    });

  const cloudDialogNode = cloudDialog === 'cloud' && (
    <CloudDialog
      cloud={cloud}
      state={cloudState}
      t={t}
      locale={locale}
      project={project}
      start={cloudStart}
      onClose={() => {
        setCloudDialog(null);
        setResetToken(null);
        setConfirmToken(null);
        afterSignIn.current = null;
      }}
      resetToken={resetToken}
      onResetDone={() => setResetToken(null)}
      confirmToken={confirmToken}
      onConfirmDone={(ok) => {
        setConfirmToken(null);
        if (ok) setMessage(t('cloud.confirmed'));
      }}
      onOpen={(id) => void openCloud(id)}
      onUpload={uploadToCloud}
      onNewFromTemplate={(p) => {
        setCloudDialog(null);
        load(
          { ...p, id: newId(), meta: { ...p.meta, date: new Date().toISOString().slice(0, 10) } },
          null,
          false,
        );
      }}
    />
  );

  // A quote sent for online acceptance: the client's page, no editor.
  const offerToken = /^#\/offer\/([\w-]+)/.exec(hash)?.[1];
  if (offerToken)
    return (
      <OfferView
        token={offerToken}
        baseUrl={cloud.client?.baseUrl}
        t={t}
        locale={locale}
        setLocale={setLocale}
      />
    );
  // The homeowner's page behind the board's QR code: a page of its own, no editor.
  const homeToken = /^#\/home\/([\w-]+)/.exec(hash)?.[1];
  if (homeToken)
    return (
      <HomeView
        token={homeToken}
        baseUrl={cloud.client?.baseUrl}
        t={t}
        locale={locale}
        setLocale={setLocale}
      />
    );
  const surveyRoute = viewer ? null : parseSurveyHash(hash);
  if (surveyRoute)
    return (
      <>
        <Survey
          route={surveyRoute}
          go={goSurvey}
          project={project}
          history={history}
          assets={assets}
          t={t}
          locale={locale}
          setLocale={setLocale}
          ai={ai}
          withAccount={withAccount}
          cloud={cloud}
          cloudState={cloudState}
          ask={ask}
          notify={setMessage}
          status={surveyStatus}
          stored={!!(libKey || live)}
          openKey={openKey}
          newSurvey={newSurvey}
          openEntry={openEntry}
          openCloud={async (id) => {
            await openCloud(id);
            if (cloud.state.live?.projectId === id) goSurvey({ screen: 'project', tab: 'rooms' });
          }}
          removeEntry={removeEntry}
          keepOnDevice={() => keepOnDevice()}
          openEditor={openEditor}
          exportPdf={() => void exportFile('pdf')}
          signIn={() => setCloudDialog('cloud')}
          upload={async () => {
            const key = libKey;
            try {
              await uploadToCloud();
            } catch (e) {
              return setMessage(t('cloud.error', { msg: (e as Error).message }));
            }
            // The cloud copy on this device replaces the local project.
            if (key && cloud.state.live) await deleteLibraryEntry(key);
          }}
        />
        {message && (
          <div className="sv-toast" role="status" onClick={() => setMessage(null)} data-testid="sv-toast">
            {message}
          </div>
        )}
        {cloudDialogNode}
        {aiGateNode}
        {confirmNode}
      </>
    );

  return (
    <div className={`app${side.folded ? ' side-folded' : ''}${palFolded ? ' pal-folded' : ''}`}>
      <header className="toolbar">
        <div className="brand">
          <img src="./logo.svg" alt="" width={22} height={22} />
          <strong>{t('app.name')}</strong>
        </div>
        <Menu
          trigger={<span className="menu-label">{t('menu.file')}</span>}
          testId="menu-file"
          items={[
            { label: t('menu.new'), onSelect: () => void onNew() },
            {
              label: t('menu.newFromTemplate'),
              onSelect: () => setShowTemplates(true),
              testId: 'new-from-template',
            },
            {
              label: t('survey.menu'),
              onSelect: () =>
                goSurvey(
                  hasSurvey(project) || libKey ? { screen: 'project', tab: 'rooms' } : { screen: 'home' },
                ),
              testId: 'survey-mode',
            },
            { label: t('menu.open'), onSelect: () => void onOpen(), shortcut: `${MOD}O` },
            {
              label: t('menu.save'),
              onSelect: () => void onSave(false),
              shortcut: `${MOD}S`,
              separatorBefore: true,
            },
            { label: t('menu.saveAs'), onSelect: () => void onSave(true), shortcut: `${MOD}⇧S` },
            {
              label: t('menu.export'),
              separatorBefore: true,
              testId: 'menu-export',
              submenu: [
                {
                  label: t('menu.exportPdf'),
                  onSelect: () => void exportFile('pdf'),
                  testId: 'export-pdf',
                },
                {
                  label: t('menu.exportPdfFull'),
                  onSelect: () => void exportFile('pdf+'),
                  testId: 'export-pdf-full',
                },
                {
                  label: t('menu.exportSvg'),
                  onSelect: () => void exportFile('svg'),
                  testId: 'export-svg',
                },
                {
                  label: t('menu.exportPng'),
                  onSelect: () => void exportFile('png'),
                  testId: 'export-png',
                },
                {
                  label: t('menu.exportDxf'),
                  onSelect: () => void exportFile('dxf'),
                  testId: 'export-dxf',
                },
              ],
            },
            {
              label: t('menu.reports'),
              testId: 'menu-reports',
              submenu: [
                {
                  label: t('menu.circuitsCsv'),
                  onSelect: () => void exportReport('circuits'),
                  testId: 'report-circuits',
                },
                {
                  label: t('menu.bomCsv'),
                  onSelect: () => void exportReport('bom'),
                  testId: 'report-bom',
                },
                {
                  label: t('menu.labelsPdf'),
                  onSelect: () => void exportReport('labels'),
                  testId: 'report-labels',
                },
                {
                  label: t('pv.labelsMenu'),
                  onSelect: () => void exportPvLabels(),
                  testId: 'report-pv-labels',
                },
                {
                  label: t('pick.menu'),
                  onSelect: () => setShowPick(true),
                  testId: 'report-pick',
                },
                ...([12, 18, 9, 24] as const).map((mm) => ({
                  label: t('menu.labelsTape', { mm }),
                  onSelect: () => void exportTapeLabels(mm),
                  testId: `report-tape-${mm}`,
                })),
                {
                  label: t('menu.terminalPlanCsv'),
                  onSelect: () => void exportReport('terminals'),
                  testId: 'report-terminals',
                },
                {
                  label: t('quote.menu'),
                  onSelect: () => setShowQuote(true),
                  testId: 'report-quote',
                },
              ],
            },
            { label: t('menu.print'), onSelect: () => void onPrint(), shortcut: `${MOD}P` },
            {
              label: t('rev.menu'),
              onSelect: () => setShowRevisions(true),
              separatorBefore: true,
              testId: 'revisions',
            },
            {
              label: t('menu.projectInfo'),
              onSelect: () => setShowInfo(true),
              testId: 'project-info',
            },
            {
              label: t('clients.menu'),
              onSelect: () => setShowClients(true),
              testId: 'cmd-clients',
            },
            ...(live
              ? [
                  {
                    label: t('share.menu'),
                    onSelect: () => setCloudDialog('share'),
                    separatorBefore: true,
                    testId: 'cloud-share',
                  },
                  {
                    label: t('cloud.leave'),
                    onSelect: () => {
                      cloud.leave(history);
                      setSavedProject(createProject());
                    },
                    testId: 'cloud-leave',
                  },
                ]
              : []),
            {
              label: t('tour.menu'),
              separatorBefore: true,
              testId: 'start-tour',
              onSelect: () => void startTour(),
            },
            {
              label: t('menu.demo'),
              testId: 'load-demo',
              onSelect: async () => {
                if (await confirmDiscard()) load(generateDemoProject(locale));
              },
            },
            {
              label: t('settings.menu'),
              onSelect: () => setShowSettings(true),
              testId: 'settings',
            },
            {
              label: t('about.menu'),
              onSelect: () => setShowAbout(true),
              testId: 'about',
            },
            {
              label: t('menu.benchmark'),
              onSelect: async () => {
                if (await confirmDiscard()) void runBenchmark(10_000);
              },
            },
          ]}
        />
        <div className="tool-group">
          <IconButton
            icon="undo"
            label={t('menu.undo')}
            shortcut={`${MOD}Z`}
            onClick={() => history.undo()}
            disabled={!history.canUndo}
          />
          <IconButton
            icon="redo"
            label={t('menu.redo')}
            shortcut={`${MOD}⇧Z`}
            onClick={() => history.redo()}
            disabled={!history.canRedo}
          />
        </div>
        <div className="tool-group">
          <IconButton
            icon="select"
            label={t('tool.select')}
            shortcut="V"
            active={tool.kind === 'select'}
            onClick={() => editor.setTool({ kind: 'select' })}
            testId="tool-select"
          />
          <IconButton
            icon="wire"
            label={t('tool.wire')}
            shortcut="W"
            active={tool.kind === 'wire'}
            onClick={() => editor.setTool({ kind: 'wire' })}
            testId="tool-wire"
          />
          <IconButton
            icon="text"
            label={t('tool.text')}
            shortcut="T"
            active={tool.kind === 'place' && tool.symbolId === 'annot.text'}
            onClick={() => editor.setTool({ kind: 'place', symbolId: 'annot.text' })}
          />
          {isPlan && (
            <IconButton
              icon="room"
              label={t('tool.room')}
              shortcut="P"
              active={tool.kind === 'room'}
              onClick={() => editor.setTool({ kind: 'room' })}
              testId="tool-room"
            />
          )}
          {isPlan && sheet.background && (
            <IconButton
              icon="ruler"
              label={t('tool.calibrate')}
              active={tool.kind === 'calibrate'}
              onClick={() => editor.setTool({ kind: 'calibrate' })}
              testId="tool-calibrate"
            />
          )}
        </div>
        <div className="tool-group">
          <IconButton
            icon="rotate"
            label={t('tool.rotate')}
            shortcut="R"
            onClick={() => editor.rotate()}
            disabled={!editor.selection.size && tool.kind !== 'place'}
          />
          <IconButton
            icon="mirror"
            label={t('tool.mirror')}
            shortcut="M"
            onClick={() => editor.mirror()}
            disabled={!editor.selection.size}
          />
          <IconButton
            icon="delete"
            label={t('tool.delete')}
            shortcut={isMac ? '⌫' : 'Suppr'}
            onClick={() => editor.deleteSelection()}
            disabled={!editor.selection.size}
          />
        </div>
        <div className="tool-group">
          <IconButton icon="fit" label={t('menu.fit')} shortcut="F" onClick={() => canvas.current?.fit()} />
          <IconButton
            icon="print"
            label={t('menu.print')}
            shortcut={`${MOD}P`}
            onClick={() => void onPrint()}
          />
        </div>
        <Menu
          trigger={<span className="menu-label">{t('menu.diagram')}</span>}
          testId="menu-diagram"
          items={[
            {
              label: t('cmd.multiLine'),
              onSelect: () => generateMultiLine(generatedFrom?.id),
              testId: 'cmd-multiline',
            },
            {
              label: t('cmd.boardLayout'),
              onSelect: () => generateBoardLayout(generatedFrom?.id),
              testId: 'cmd-board-layout',
            },
            {
              label: t('pv.menu'),
              onSelect: () => setShowPv(true),
              testId: 'cmd-pv',
            },
            ...(hasSurvey(project)
              ? [
                  {
                    label: t('survey.generate'),
                    onSelect: generateFromSurveyCmd,
                    testId: 'cmd-from-survey',
                  },
                ]
              : []),
            {
              label: t('cmd.numberWires'),
              onSelect: () => run(t('tx.numberWires'), numberWiresOps(project, sheet.id)),
              testId: 'cmd-number-wires',
            },
            {
              label: t('cmd.numberTerminals'),
              onSelect: () => run(t('tx.numberTerminals'), numberTerminalsOps(project)),
              testId: 'cmd-number-terminals',
            },
            {
              label: t('macro.save'),
              onSelect: () =>
                editor.selection.size ? setAskMacroName(true) : setMessage(t('macro.selectFirst')),
              testId: 'save-macro',
              separatorBefore: true,
            },
            {
              label: t('cat.assign'),
              separatorBefore: true,
              testId: 'menu-catalogue',
              submenu: BRANDS.map((brand, i) => ({
                label: brand,
                onSelect: () => {
                  const r = assignBrandOps(project, brand, true);
                  run(t('tx.assignBrand', { brand }), r.ops);
                  if (r.ops.length)
                    setMessage(t('cat.assigned', { n: r.ops.length, missing: r.missing.length, brand }));
                },
                testId: `brand-${i}`,
              })),
            },
            {
              label: t('cmd.insert'),
              testId: 'menu-insert',
              submenu: INSERT_TEMPLATES.map((tpl) => ({
                label: pick(tpl.name, locale),
                onSelect: () => insertTemplate(tpl),
                testId: `insert-${tpl.id}`,
              })),
            },
          ]}
        />
        <Menu
          trigger={<span className="menu-label">{t('menu.inspection')}</span>}
          testId="menu-inspection"
          items={[
            {
              label: t('measure.menu'),
              onSelect: () => setAfterDialog('measurements'),
              testId: 'cmd-measurements',
            },
            {
              label: t('inspection.menu'),
              onSelect: () => setAfterDialog('inspections'),
              testId: 'cmd-inspections',
            },
            {
              label: t('signoff.menu'),
              onSelect: () => setAfterDialog('signoff'),
              testId: 'cmd-signoff',
            },
            {
              label: t('energy.menu'),
              onSelect: () => setShowEnergy(true),
              separatorBefore: true,
              testId: 'cmd-energy',
            },
            {
              label: t('invoice.menu'),
              onSelect: () => {
                setInvoiceQuote(undefined);
                setAfterDialog('invoices');
              },
              separatorBefore: true,
              testId: 'cmd-invoices',
            },
          ]}
        />
        <Menu
          trigger={<span className="menu-label ai-label">{t('ai.menu')}</span>}
          testId="menu-ai"
          items={[
            {
              label: t('ai.assistant'),
              onSelect: () =>
                void withAccount('assist', () =>
                  openSection('assistant', () =>
                    document.querySelector<HTMLTextAreaElement>('[data-testid="assist-text"]')?.focus(),
                  ),
                ),
              testId: 'ai-assist',
            },
            {
              label: t('ai.board'),
              onSelect: () => void withAccount('board', () => setAiDialog('board')),
              testId: 'ai-board',
            },
            {
              label: t('ai.sketch'),
              onSelect: () => void withAccount('sketch', () => setAiDialog('sketch')),
              testId: 'ai-sketch',
            },
          ]}
        />
        <div className="tool-group">
          <button
            className={`text-btn${panel === 'circuits' ? ' active' : ''}`}
            onClick={() => setPanel(panel === 'circuits' ? null : 'circuits')}
            data-testid="toggle-circuits"
          >
            {t('panel.circuits')}
          </button>
          <button
            className={`text-btn${panel === 'checks' ? ' active' : ''}`}
            onClick={() => setPanel(panel === 'checks' ? null : 'checks')}
            data-testid="toggle-checks"
          >
            {t('panel.checks', { std: t(`std.${project.meta.standard}`) })}
            <span className={`count${nErrors ? ' bad' : nWarnings ? ' meh' : ' good'}`}>
              {nErrors || nWarnings || '✓'}
            </span>
          </button>
        </div>
        <span className="grow" />
        <label className="lang">
          <span className="sr-only">{t('menu.language')}</span>
          <select value={locale} onChange={(e) => setLocale(e.target.value as Locale)} data-testid="lang">
            {Object.entries(LOCALES).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </label>
        <AccountButton
          cloud={cloud}
          state={cloudState}
          t={t}
          onOpen={(view) => {
            setCloudStart(view);
            setCloudDialog('cloud');
          }}
        />
      </header>

      {recovery && (
        <div className="banner" role="alert">
          <Icon name="info" />
          <div>
            <strong>{t('recovery.title')}</strong>{' '}
            {t('recovery.body', {
              name: recovery.name,
              date: new Date(recovery.savedAt).toLocaleString(localeTag(locale)),
            })}
            {recovery.where === 'account' && ` ${t('recovery.fromAccount')}`}
            {recoveries.length > 1 && (
              <>
                {' '}
                <select
                  aria-label={t('recovery.pick')}
                  value={recoveries.indexOf(recovery)}
                  onChange={(e) => setRecoveryPick(Number(e.target.value))}
                  data-testid="recovery-pick"
                >
                  {recoveries.map((c, i) => (
                    <option key={c.id} value={i}>
                      {c.name} · {new Date(c.savedAt).toLocaleString(localeTag(locale))}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
          <button className="primary" onClick={() => void restore()} data-testid="recovery-restore">
            {t('recovery.restore')}
          </button>
          <button
            onClick={() => {
              forgetAutosave(recovery.id);
              setRecoveryPick(0);
            }}
            data-testid="recovery-delete"
          >
            {t('recovery.delete')}
          </button>
          <button onClick={() => setRecoveryDismissed(true)}>{t('recovery.dismiss')}</button>
        </div>
      )}

      <Palette
        sheetKind={sheet.kind}
        locale={locale}
        t={t}
        unplaced={isPlan ? unplaced : undefined}
        activePreset={tool.kind === 'place' ? tool.preset?.tag : undefined}
        onPlace={(u) => {
          editor.setTool({ kind: 'place', symbolId: u.symbolId, preset: { tag: u.tag, props: u.props } });
          document.querySelector<HTMLCanvasElement>('.canvas')?.focus({ preventScroll: true });
        }}
        active={tool.kind === 'place' && !tool.preset ? tool.symbolId : null}
        onPick={(id) => {
          editor.setTool(
            tool.kind === 'place' && tool.symbolId === id
              ? { kind: 'select' }
              : { kind: 'place', symbolId: id },
          );
          document.querySelector<HTMLCanvasElement>('.canvas')?.focus({ preventScroll: true });
        }}
        userSymbolsKey={project.symbols}
        onNewSymbol={() => setSymbolEditor({})}
        onEditSymbol={(id) => setSymbolEditor({ id })}
        macros={macros}
        onInsertMacro={insertMacro}
        onDeleteMacro={async (m) => {
          if (
            await ask({
              title: t('macro.delete'),
              body: t('macro.deleteConfirm', { name: m.name }),
              confirm: t('macro.delete'),
              danger: true,
            })
          )
            saveMacros(macros.filter((x) => x.id !== m.id));
        }}
        onLibrary={(a) => void library(a)}
        teamItems={teamItems}
        onInsertTeamItem={(item) => insertTeamItem(item)}
        {...(cloudState.team && cloudState.team.role !== 'viewer'
          ? { onShareToTeam: () => void shareToTeam() }
          : {})}
        folded={palFolded}
        onToggleFold={() => setPalFolded(!palFolded)}
      />

      <main className="stage">
        <Canvas
          ref={canvas}
          editor={editor}
          onZoom={setZoom}
          onEditElement={() => openSection('selection', () => inspector.current?.focusFirst())}
          label={hint}
          images={assets.images}
          locale={project.meta.locale}
          diff={diff}
          {...(peers ? { peers } : {})}
          pins={pinsOn}
          {...(picking ? { onPick: pickPin } : {})}
        />
        {/* Pinned to the bottom of the canvas, above the bottom panel and the sheet tabs. */}
        <div className="hint-anchor">
          <p className="hint">{hint}</p>
        </div>
        {comparing && diff && (
          <div className="sheet-banner diff" role="status" data-testid="diff-banner">
            <span>
              {comparingTo
                ? t('rev.diffBetween', { from: comparing.index, to: comparingTo.index, ...diff.counts })
                : t('rev.diff', { index: comparing.index, ...diff.counts })}
            </span>
            <button onClick={() => void showChanges()} data-testid="diff-list">
              {t('rev.list')}
            </button>
            <button onClick={() => compare(null, null)} data-testid="diff-close">
              {t('rev.diffClose')}
            </button>
          </div>
        )}
        {changes && comparing && (
          <ChangesDialog
            t={t}
            title={changesLabel()}
            report={changes.report}
            onSheet={(id) => {
              if (comparingTo ? comparingTo.snapshot.sheets[id] : project.sheets[id]) editor.setSheet(id);
              setChanges(null);
            }}
            onPdf={() => void exportChanges(comparing.id, comparingTo?.id ?? null)}
            onClose={() => setChanges(null)}
          />
        )}
        {!comparing && previewed && diff && (
          <div className="sheet-banner preview" role="status" data-testid="preview-banner">
            <span>{t('ai.previewBanner', { ...diff.counts })}</span>
          </div>
        )}
        {!comparing && !previewed && generatedFrom && (
          <div className={`sheet-banner${isStale ? ' stale' : ''}`} role="status" data-testid="ml-banner">
            <span>
              {isStale
                ? t('ml.stale', { name: generatedFrom.name })
                : t('ml.generated', { name: generatedFrom.name })}
            </span>
            {isStale && (
              <button className="primary" onClick={() => regenerate(sheet)} data-testid="ml-update">
                {t('ml.update')}
              </button>
            )}
          </div>
        )}
        {panel && (
          <BottomPanel
            tab={panel}
            onTab={setPanel}
            editor={editor}
            project={project}
            analysis={analysis}
            findings={findings}
            t={t}
            locale={locale}
            onShow={show}
            onGoto={(f, b, pick) => gotoFinding(f, b, pick)}
            {...(ruleReporter ? { reporter: ruleReporter } : {})}
            onFix={(f) =>
              void withAccount('assist', () =>
                openSection('assistant', () =>
                  setAssistRequest((r) => ({
                    text: fixRequest(f, cl),
                    fix: { ruleId: f.ruleId, targets: f.targets },
                    n: (r?.n ?? 0) + 1,
                    projectId: project.id,
                  })),
                ),
              )
            }
          />
        )}
        <SheetTabs editor={editor} project={project} t={t} confirm={ask} notify={setMessage} />
      </main>

      <Sidebar
        t={t}
        state={side}
        onChange={setSide}
        items={[
          {
            id: 'selection',
            icon: 'sliders',
            title: t('panel.selection'),
            badge: editor.selection.size,
            content: (
              <Inspector
                ref={inspector}
                part="selection"
                editor={editor}
                project={project}
                sheet={sheet}
                locale={locale}
                t={t}
                onProjectInfo={() => setShowInfo(true)}
                onImportBackground={() => void importBackground()}
                {...(canScan ? { onScanRooms: () => void scanRooms() } : {})}
                onCalibrate={() => editor.setTool({ kind: 'calibrate' })}
                assets={assets}
                notify={setMessage}
              />
            ),
          },
          {
            id: 'assistant',
            icon: 'spark',
            title: t('side.assistant'),
            content: cloudState.me ? (
              <AssistantPanel
                // A conversation belongs to one project: its proposals are ops on that project.
                key={project.id}
                ai={ai}
                t={t}
                locale={cl}
                uiLocale={locale}
                project={project}
                sheetId={sheet.id}
                mod={MOD}
                app={`${APP_VERSION} ${platform.kind}`}
                onApply={(ops, s) => {
                  editor.setPreview(null);
                  run(t('tx.assistant'), ops, s);
                }}
                onPreview={previewProposal}
                {...(assistRequest?.projectId === project.id
                  ? {
                      request: {
                        text: assistRequest.text,
                        n: assistRequest.n,
                        ...(assistRequest.fix ? { fix: assistRequest.fix } : {}),
                      },
                    }
                  : {})}
              />
            ) : (
              <div className="assistant-panel signed-out" data-testid="assist-signed-out">
                <p className="muted small">{t('side.assistSignedOut')}</p>
                <button
                  className="wide-btn"
                  onClick={() => void withAccount('assist', () => openSection('assistant'))}
                  data-testid="assist-signin"
                >
                  {t('aiGate.signIn')}
                </button>
              </div>
            ),
          },
          {
            id: 'sheet',
            icon: 'sheet',
            title: t('panel.sheet'),
            content: (
              <Inspector
                part="sheet"
                editor={editor}
                project={project}
                sheet={sheet}
                locale={locale}
                t={t}
                onProjectInfo={() => setShowInfo(true)}
                onImportBackground={() => void importBackground()}
                {...(canScan ? { onScanRooms: () => void scanRooms() } : {})}
                onCalibrate={() => editor.setTool({ kind: 'calibrate' })}
                assets={assets}
                notify={setMessage}
              />
            ),
          },
        ]}
      />

      <footer className="status">
        <span>{t(`sheetKind.${sheet.kind}`)}</span>
        <span>{t('status.elements', { n: nElements.toLocaleString(locale) })}</span>
        <span>{t('status.wires', { n: nWires.toLocaleString(locale) })}</span>
        <span>{t('status.zoom', { pct: Math.round((zoom / 3.78) * 100) })}</span>
        {live ? (
          <span className={live.online ? '' : 'dirty'} data-testid="cloud-status">
            {live.online
              ? live.pending
                ? t('cloud.syncing', { n: live.pending })
                : t('cloud.synced')
              : t('cloud.offline', { n: live.pending })}
            {live.presence.length > 1 &&
              ` · ${t('cloud.with', {
                names: live.presence
                  .filter((c) => c.userId !== cloudState.me?.user.id)
                  .map((c) => c.name)
                  .filter((n, i, a) => a.indexOf(n) === i)
                  .join(', '),
              })}`}
            {live.role === 'viewer' && ` · ${t('cloud.readOnly')}`}
          </span>
        ) : viewer ? (
          <span data-testid="viewer-status">{t('viewer.status')}</span>
        ) : (
          <span className={dirty ? 'dirty' : ''}>{dirty ? t('status.unsaved') : (file?.name ?? '')}</span>
        )}
        {dirty && autosavedAt && <span>{t('status.autosaved', { time: time(autosavedAt) })}</span>}
        <button
          className={`status-btn${nErrors ? ' bad' : ''}`}
          onClick={() => setPanel('checks')}
          data-testid="status-checks"
        >
          {t('status.checks', { e: nErrors, w: nWarnings, std: t(`std.${project.meta.standard}`) })}
        </button>
        <span className="grow" role="status">
          {message}
        </span>
        <span>{t(`status.platform.${platform.kind}`)}</span>
      </footer>

      {showEnergy && (
        <EnergyDialog
          project={project}
          analysis={analysis}
          t={t}
          locale={locale}
          savePdf={(bytes, name) => void saveExport(bytes, name, 'pdf', 'application/pdf')}
          onClose={() => setShowEnergy(false)}
        />
      )}
      {showPick && (
        <PickListDialog
          project={project}
          t={t}
          locale={locale}
          save={(bytes, name, ext, mime) => void saveExport(bytes, name, ext, mime)}
          notify={setMessage}
          onClose={() => setShowPick(false)}
        />
      )}
      {showClients && (
        <ClientsDialog
          t={t}
          locale={locale}
          cloud={cloud}
          currentId={project.id}
          openCloud={(id) => {
            setShowClients(false);
            void openCloud(id);
          }}
          saveFile={(bytes, name) => void saveExport(bytes, name, 'ics', 'text/calendar')}
          onClose={() => setShowClients(false)}
        />
      )}
      {showInfo && (
        <ProjectInfoDialog
          meta={project.meta}
          focusMissing={showInfo === 'missing'}
          t={t}
          onClose={() => setShowInfo(false)}
          onSave={(patch: Partial<ProjectMeta>) =>
            history.dispatch({ label: t('tx.meta'), ops: [{ op: 'meta', patch }] })
          }
        />
      )}
      {editor.pendingCalibration && sheet.background && (
        <CalibrateDialog
          t={t}
          measuredMm={Math.hypot(
            editor.pendingCalibration.b.x - editor.pendingCalibration.a.x,
            editor.pendingCalibration.b.y - editor.pendingCalibration.a.y,
          )}
          scale={sheet.planScale ?? 50}
          onCancel={() => editor.clearCalibration()}
          onApply={(meters, scale) => {
            const cal = editor.pendingCalibration!;
            const withScale =
              scale !== sheet.planScale
                ? { ...project, sheets: { ...project.sheets, [sheet.id]: { ...sheet, planScale: scale } } }
                : project;
            history.dispatch({
              label: t('tx.calibrate'),
              ops: [
                ...(scale !== sheet.planScale
                  ? [
                      {
                        op: 'update' as const,
                        coll: 'sheets' as const,
                        id: sheet.id,
                        patch: { planScale: scale },
                      },
                    ]
                  : []),
                ...calibrateOps(withScale, sheet.id, cal.a, cal.b, meters),
              ],
            });
            editor.clearCalibration();
          }}
        />
      )}
      {aiDialog === 'board' && (
        <BoardImportDialog
          ai={ai}
          platform={platform}
          t={t}
          onClose={() => setAiDialog(null)}
          onImport={importBoard}
        />
      )}
      {aiDialog === 'sketch' && (
        <SketchDialog
          ai={ai}
          platform={platform}
          t={t}
          onClose={() => setAiDialog(null)}
          onCreate={createFromSketch}
        />
      )}
      {cloudDialogNode}
      {aiGateNode}
      {cloudDialog === 'share' && live && (
        <ShareDialog
          cloud={cloud}
          t={t}
          project={project}
          projectId={live.projectId}
          canShare={live.role !== 'viewer'}
          onClose={() => setCloudDialog(null)}
          onHandover={handover}
          qrSvg={async (text) => (await exporters()).qrSvg(text)}
        />
      )}
      {viewer && (
        <div className={`viewer-banner${viewer.review ? ' review' : ''}`} data-testid="viewer-banner">
          {viewer.revision ? t('viewer.revision', { index: viewer.revision }) : t('viewer.live')}
          {viewer.review && (
            <>
              <span data-testid="review-count">{t('review.banner', { n: viewer.review.pins.length })}</span>
              <button
                className={picking ? 'primary' : ''}
                aria-pressed={picking}
                onClick={() => setPicking(!picking)}
                data-testid="review-pin"
              >
                {picking ? t('review.picking') : t('review.pin')}
              </button>
              <button onClick={() => setShowPins(true)} data-testid="review-list">
                {t('review.list')}
              </button>
            </>
          )}
          <button onClick={() => void exportFile('pdf+')}>{t('viewer.download')}</button>
        </div>
      )}
      {pinDraft && viewer?.review && (
        <PinDialog
          t={t}
          n={viewer.review.pins.length + 1}
          initialRef={pinDraft.ref}
          onCancel={() => setPinDraft(null)}
          onSave={async (v) => {
            const review = viewer.review!;
            const pin = await new CloudClient(cloud.client!.baseUrl).addPin(review.token, {
              ...v,
              sheetId: pinDraft.sheetId,
              x: pinDraft.x,
              y: pinDraft.y,
            });
            setViewer({ ...viewer, review: { ...review, pins: [...review.pins, pin] } });
            setPinDraft(null);
          }}
        />
      )}
      {showPins && viewer?.review && (
        <PinsDialog
          t={t}
          pins={viewer.review.pins}
          sheetName={(id) => project.sheets[id]?.name ?? '?'}
          onShow={(p) => {
            if (project.sheets[p.sheetId]) editor.setSheet(p.sheetId);
            setShowPins(false);
          }}
          onDelete={(p) => {
            const review = viewer.review!;
            void new CloudClient(cloud.client!.baseUrl)
              .deletePin(review.token, p.id)
              .then(() =>
                setViewer({
                  ...viewer,
                  review: { ...review, pins: review.pins.filter((x) => x.id !== p.id) },
                }),
              )
              .catch((e: Error) => setMessage(e.message));
          }}
          onClose={() => setShowPins(false)}
        />
      )}
      {showWelcome && !recovery && !viewer && (
        <WelcomeDialog
          t={t}
          onClose={closeWelcome}
          onTemplate={() => {
            closeWelcome();
            setShowTemplates(true);
          }}
          onDemo={() => {
            closeWelcome();
            load(generateDemoProject(locale));
          }}
          onTour={() => {
            closeWelcome();
            void startTour();
          }}
        />
      )}
      {tour && (
        <Tour
          app={tourApp}
          t={t}
          mod={MOD}
          onClose={(finished) => {
            setTour(false);
            platform.setPref('tourDone', finished ? 'finished' : 'closed');
          }}
          onRestart={() => {
            load(createProject({ locale }));
            setPanel(null);
          }}
        />
      )}
      {showAbout && (
        <AboutDialog
          t={t}
          platform={platform}
          aiMode={ai.mode}
          locale={locale}
          onClose={() => setShowAbout(false)}
        />
      )}
      {showRevisions && (
        <RevisionsDialog
          project={project}
          t={t}
          locale={locale}
          onClose={() => setShowRevisions(false)}
          onCreate={(d) => createRevision(d)}
          onCompare={(from, to) => {
            compare(from, to);
            setShowRevisions(false);
          }}
          onChangesPdf={(from, to) => void exportChanges(from, to)}
          onRestore={(id) => void restoreRevision(id)}
        />
      )}
      {symbolEditor && (
        <SymbolEditorDialog
          t={t}
          locale={cl}
          {...(symbolEditor.id && project.symbols[symbolEditor.id]
            ? { initial: project.symbols[symbolEditor.id] }
            : {})}
          onClose={() => setSymbolEditor(null)}
          onSave={saveSymbol}
        />
      )}
      {askMacroName && (
        <NameDialog
          title={t('macro.save')}
          label={t('macro.name')}
          ok={t('dialog.ok')}
          cancel={t('dialog.cancel')}
          onClose={() => setAskMacroName(false)}
          onOk={saveMacro}
        />
      )}
      {showTemplates && (
        <TemplateDialog
          t={t}
          locale={locale}
          onClose={() => setShowTemplates(false)}
          onCreate={(tpl) => void newFromTemplate(tpl)}
        />
      )}
      {showQuote && (
        <QuoteDialog
          project={project}
          t={t}
          locale={locale}
          platform={platform}
          baseName={baseName}
          load={exporters}
          save={saveExport}
          client={account}
          notify={setMessage}
          onInvoice={(quote) => {
            setShowQuote(false);
            setInvoiceQuote(quote);
            setAfterDialog('invoices');
          }}
          onOffer={(quote) => setOfferQuote(quote)}
          onClose={() => setShowQuote(false)}
        />
      )}
      {offerQuote && (
        <OfferDialog
          project={project}
          quote={offerQuote}
          t={t}
          locale={locale}
          platform={platform}
          client={account}
          load={exporters}
          notify={setMessage}
          onClose={() => setOfferQuote(null)}
        />
      )}
      {afterDialog === 'signoff' && (
        <SignoffDialog
          project={project}
          t={t}
          locale={locale}
          assets={assets}
          readOnly={history.readOnly}
          dispatch={(label, ops) => history.dispatch({ label, ops })}
          notify={setMessage}
          handoverPack={() => void handover(null)}
          onClose={() => setAfterDialog(null)}
        />
      )}
      {afterDialog === 'measurements' && (
        <MeasurementsDialog
          project={project}
          analysis={analysis}
          t={t}
          locale={locale}
          readOnly={history.readOnly}
          dispatch={(label, ops) => history.dispatch({ label, ops })}
          exportCsv={() => void exportMeasurementsCsv()}
          onClose={() => setAfterDialog(null)}
        />
      )}
      {afterDialog === 'inspections' && (
        <InspectionsDialog
          project={project}
          t={t}
          locale={locale}
          ai={ai}
          withAccount={withAccount}
          assets={assets}
          readOnly={history.readOnly}
          notify={setMessage}
          dispatch={(label, ops) => history.dispatch({ label, ops })}
          ask={ask}
          exportPack={(id) => void exportReinspection(id)}
          onRemediation={(id) => setRemedyFor(id)}
          pendingPins={pendingPins.length}
          onImportPins={() => void importPins()}
          onSince={(revId) => {
            compare(revId, null);
            setAfterDialog(null);
          }}
          onClose={() => setAfterDialog(null)}
        />
      )}
      {remedyFor && project.inspections[remedyFor] && (
        <RemediationDialog
          project={project}
          inspectionId={remedyFor}
          t={t}
          locale={locale}
          platform={platform}
          baseName={baseName}
          client={account}
          load={exporters}
          save={saveExport}
          onInvoice={(quote) => {
            setRemedyFor(null);
            setInvoiceQuote(quote);
            setAfterDialog('invoices');
          }}
          onClose={() => setRemedyFor(null)}
        />
      )}
      {afterDialog === 'invoices' && (
        <InvoiceDialog
          project={project}
          t={t}
          locale={locale}
          platform={platform}
          baseName={baseName}
          readOnly={history.readOnly}
          load={exporters}
          save={saveExport}
          dispatch={(label, ops) => history.dispatch({ label, ops })}
          notify={setMessage}
          fromQuote={invoiceQuote}
          client={account}
          workspace={invoiceWorkspace}
          canEmail={cloudState.mail}
          onClose={() => setAfterDialog(null)}
        />
      )}
      {dxf && (
        <DxfImportDialog
          t={t}
          locale={cl}
          drawing={dxf.drawing}
          area={(() => {
            const { w, h } = paperSize(sheet);
            return { width: w - 30, height: h - 60 };
          })()}
          onCancel={() => setDxf(null)}
          onImport={(plan, scale) => void importVectorPlan(plan, scale)}
        />
      )}
      {showSettings && (
        <SettingsDialog t={t} platform={platform} locale={locale} onClose={() => setShowSettings(false)}>
          <BackupSection t={t} platform={platform} backup={backup} locale={locale} />
        </SettingsDialog>
      )}
      {showPv && (
        <PvDialog
          project={project}
          t={t}
          sheetId={sheet.id}
          onClose={() => setShowPv(false)}
          onCreate={createPv}
        />
      )}
      {roomScan && (
        <RoomScanDialog
          t={t}
          locale={locale}
          scan={roomScan}
          area={(() => {
            const { w, h } = paperSize(sheet);
            return { width: w - 30, height: h - 60 };
          })()}
          onCancel={() => setRoomScan(null)}
          onImport={(plan, scale) => void importVectorPlan(plan, scale)}
        />
      )}
      {confirmNode}
    </div>
  );
}
